import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import { useDarkMode } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import {
  useHikvisionAccessStore,
  fetchYardAccessEvents,
  fetchYardAccessEvent,
  createManualAccessEvent,
  fetchHikvisionDevices,
  fetchControllerHistoryAndWait,
  syncControllerEventsToDb,
  deleteYardAccessEvents,
  fetchYardSyncStatus,
} from "../../../store/oasis/hikvisionAccessStore";
import type {
  YardAccessEvent,
  HikvisionDevice,
  HistorySyncSummary,
} from "../../../store/oasis/hikvisionAccessStore";
import useTruckYardRole, {
  resolveYardFilter,
  resolveMultiYardScopedRequest,
} from "../../../hooks/useTruckYardRole";
import MultiSelectFilter from "../components/MultiSelectFilter";
import { resolveInspectionLink } from "./inspectionLink";
import { openYardGate } from "../openYardGate";
import {
  LIVE_SIGNAL_COALESCE_MS,
  isLiveSignalHealthy,
  liveSignalYardIds,
  pollIntervalMs,
  shouldTriggerRefresh,
} from "./liveSignal";
import { ref as rtdbRef, onValue } from "firebase/database";
import { database } from "../../../firebaseConfig";
import ManualRegistrationFields from "../components/ManualRegistrationFields";
import SpecialTagBadge from "../../../components/inspections/SpecialTagBadge";
import {
  emptyManualRegistrationForm,
  isUnitMissing,
  resolveRegistrationPlate,
  validateManualRegistration,
  type ManualRegistrationErrors,
  type ManualRegistrationFormData,
} from "../components/manualRegistration";
import {
  eventSpecialTag,
  specialTagCheckMode,
} from "../specialTags";
import NoAssignedYardsNotice from "../../../components/base/NoAssignedYardsNotice";
import { useIsReseller } from "../../../hooks/useIsReseller";
import {
  BulkActionBar,
  type BulkAction,
} from "../../../Layouts/reseller-admin/shared";
import {
  fetchTruckYardOptions,
  type TruckYardOption,
} from "../../../utils/truckYardOptions";
import {
  useTruckYardStore,
  type TruckYard,
  type TruckYardUser,
  type TruckYardVehicle,
} from "../../../store/truckYardStore";
import { useGroupsStore, type Group } from "../../../store/admin/groupsStore";
import { useAllTruckYardVehicles } from "../../../features/truckYard/queries";
import { useAllGroups } from "../../../features/catalogs/queries";
import {
  fetchYardInspections,
  type YardInspection,
} from "../../../store/oasis/yardInspectionsStore";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";
import { getWaterTypeLabel } from "../components/waterTypes";
import Swal from "sweetalert2";
import ExportButtons from "../../reports/components/ExportButtons";
import type { ReportTableColumn } from "../../reports/components/ReportPointsTable";
import {
  DEFAULT_YARD_TIME_ZONE,
  yardDateBounds,
  yardTodayDateInput,
} from "../../../utils/yardDay";
import { escapeHtml } from "../../../utils/escapeHtml";

// ─── Types ───────────────────────────────────────────────────────────────────

// Flattened, display-ready shape for the Excel/PDF export (reuses the reports'
// exportHistoric utility). Mirrors the on-screen table columns.
interface AccessEventExportRow {
  id: string;
  type: string;
  vehicle: string;
  driver: string;
  yard: string;
  datetime: string;
  result: string;
  source: string;
  alerts: number;
}

interface ThemeClasses {
  bg: string;
  border: string;
  text: string;
  textSecondary: string;
  cardBg: string;
  cardBorder: string;
  buttonHover: string;
  buttonText: string;
  buttonTextHover: string;
}

const PAGE_SIZE = 12;

// grid + list are card layouts; table is the dense tabular view that also hosts
// the bulk row selection + delete controls (admins / reseller admins only).
type AccessView = "grid" | "list" | "table";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function eventTypeLabel(type: YardAccessEvent["event_type"], t: TFunction) {
  return type === "entry" ? t("access.eventType.entry") : t("access.eventType.exit");
}

function eventTypeClasses(t: YardAccessEvent["event_type"]) {
  return t === "entry"
    ? "bg-green-500/15 text-green-400 border border-green-500/30"
    : "bg-red-500/15 text-red-400 border border-red-500/30";
}

function resultLabel(r: YardAccessEvent["event_result"], t: TFunction) {
  if (r === "granted") return t("access.result.granted");
  if (r === "denied") return t("access.result.denied");
  return t("access.result.unknown");
}

function resultClasses(r: YardAccessEvent["event_result"]) {
  if (r === "granted") return "bg-green-500/15 text-green-400 border border-green-500/30";
  if (r === "denied") return "bg-red-500/15 text-red-400 border border-red-500/30";
  return "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30";
}

// ─── Repeated read ───────────────────────────────────────────────────────────
//
// The direction always comes off the reader that fired. What the backend does
// flag is a second read in the same direction with no opposite one between it
// and the last — usually one pass read twice. The movement stands as reported;
// this badge just asks the operator to confirm there was no extra pass.

function SuspiciousBadge({ event }: { event: YardAccessEvent }) {
  const { t } = useTranslation();
  if (!event.suspicious) return null;
  return (
    <span
      title={event.suspicious_reason || undefined}
      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-yellow-500/15 text-yellow-400 border border-yellow-500/30"
    >
      <i className="ri-error-warning-line text-xs" aria-hidden="true"></i>
      {t("access.suspicious.badge")}
    </span>
  );
}

// El evento salió de uno de los dos tags compartidos del patio (proveedores o
// pipas sin tag), no de la tarjeta de una unidad. Va en la fila porque cambia
// cómo se lee todo lo demás: para el de proveedores, que no haya checks no es
// un hueco sino la regla.
function EventSpecialTagBadge({ event }: { event: YardAccessEvent }) {
  const kind = eventSpecialTag(event);
  return kind ? <SpecialTagBadge kind={kind} /> : null;
}

// Full explanation for the detail modal. The reason text is written by the
// backend (it knows how long ago the previous read was), so it is rendered
// verbatim rather than reconstructed here.
function SuspiciousNotice({ event }: { event: YardAccessEvent }) {
  const { t } = useTranslation();
  if (!event.suspicious) return null;
  // Rows stored before the direction inference was removed still carry
  // `presence_inverted` and a reason written in those terms. Until the
  // migration rewrites them, title them for what they actually are instead of
  // putting "repeated read" above a paragraph that talks about a correction.
  const legacy = event.suspicious_code === "presence_inverted";
  // The reader and the stored direction normally agree. They don't when the
  // exit button fires, or on ISAPI-direct events that state their own
  // direction — and on those legacy rows. Show both only when they differ.
  const readerDirection = event.reader_direction;
  const disagrees = !!readerDirection && readerDirection !== event.event_type;
  return (
    <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 space-y-2">
      <div className="flex items-center gap-2 text-yellow-400 font-medium text-sm">
        <i className="ri-error-warning-line text-sm" aria-hidden="true"></i>
        {t(legacy ? "access.suspicious.titleLegacy" : "access.suspicious.title")}
      </div>
      {disagrees ? (
        <div className="text-xs text-gray-300">
          {t("access.suspicious.readerSaid")}:{" "}
          <strong>{eventTypeLabel(readerDirection, t)}</strong>
          {" · "}
          {t("access.suspicious.weRecorded")}:{" "}
          <strong>{eventTypeLabel(event.event_type, t)}</strong>
        </div>
      ) : null}
      {event.suspicious_reason ? (
        <p className="text-xs text-gray-300 leading-relaxed">{event.suspicious_reason}</p>
      ) : null}
      <p className="text-xs text-yellow-300/80">{t("access.suspicious.whatToDo")}</p>
    </div>
  );
}

// ─── Access-type quick filter ────────────────────────────────────────────────
// One control spanning the two independent dimensions of an access event: the
// movement (entry / exit, from `event_type`) and the credential decision
// (granted / denied / unknown, from `event_result`). They are shown as two
// labelled groups in the same dropdown so it reads as a single "tipo de acceso"
// choice without hiding that they answer different questions.
type AccessTypeFilter = "entry" | "exit" | "granted" | "denied" | "unknown";

function matchesAccessType(e: YardAccessEvent, filter: AccessTypeFilter) {
  if (filter === "entry" || filter === "exit") return e.event_type === filter;
  return e.event_result === filter;
}

function sourceLabel(s: YardAccessEvent["source"], t: TFunction) {
  if (s === "hikvision") return t("access.source.hikvision");
  if (s === "manual") return t("access.source.manual");
  if (s === "sync") return t("access.source.sync");
  return t("access.source.qr");
}

// Firestore Timestamps arrive as { _seconds, _nanoseconds } over JSON; raw
// ISO strings and Date instances are also possible depending on the path.
function parseTimestamp(ts: unknown): Date | null {
  if (!ts) return null;
  if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? null : ts;
  if (typeof ts === "string" || typeof ts === "number") {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof ts === "object") {
    const obj = ts as Record<string, unknown>;
    const sec =
      typeof obj._seconds === "number"
        ? (obj._seconds as number)
        : typeof obj.seconds === "number"
          ? (obj.seconds as number)
          : null;
    const nsec =
      typeof obj._nanoseconds === "number"
        ? (obj._nanoseconds as number)
        : typeof obj.nanoseconds === "number"
          ? (obj.nanoseconds as number)
          : 0;
    if (sec !== null) {
      const d = new Date(sec * 1000 + Math.floor(nsec / 1e6));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof (obj as { toDate?: () => Date }).toDate === "function") {
      const d = (obj as { toDate: () => Date }).toDate();
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  return null;
}

function formatTimestamp(ts: unknown): string {
  const d = parseTimestamp(ts);
  if (!d) return "—";
  return d.toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
}

// El día en curso, no "ayer → hoy". El default abarcaba dos días: el operador
// que abría la pantalla veía la jornada de ayer mezclada con la de hoy y las
// píldoras de alerta contaban sobre las dos, así que "3 rojas" no describía
// ningún turno. Los inputs Desde/Hasta siguen permitiendo ampliarlo a mano.
//
// El día se calcula en la zona del PATIO, no en la del navegador: es la fecha
// que se le manda al backend, y el backend la lee como hora de pared del patio
// (yardDateRange.js). Con `toDateInput(new Date())` un admin conectado desde
// otra zona pedía el día equivocado y veía una pantalla vacía sin ninguna
// indicación de por qué.
function defaultDateRange(timeZone?: string): { from: string; to: string } {
  const today = yardTodayDateInput(timeZone);
  return { from: today, to: today };
}

// ─── Alerts ───────────────────────────────────────────────────────────────────
//
// Derived client-side from event + vehicle catalog so the rules apply
// retroactively to historical events without a Firestore backfill.

export type AlertLevel = "red" | "yellow" | "none";
export type AlertReason =
  | "manual"
  | "no_identifier"
  | "vehicle_not_found"
  // Pipa sin tag que todavía no pasó por el registro manual de Inspecciones.
  // Ocupa el lugar de `no_identifier` / `vehicle_not_found` (specialTags.ts).
  | "untagged_pending"
  | "yard_mismatch"
  | "vehicle_type_mismatch"
  | "no_inspection"
  | "inspection_unknown"
  | "inspection_unverified"
  | "no_vigilante"
  | "exit_no_entry"
  | "exit_no_entry_ts";

export interface EventAlert {
  key: AlertReason;
  level: Exclude<AlertLevel, "none">;
  message: string;
  icon: string;
}

export interface DeriveAlertsContext {
  vehiclesById: Map<string, TruckYardVehicle>;
  inspectionsById?: Map<string, YardInspection> | null;
  // False during the initial fetch so the inspection-dependent checks don't
  // flash transient false positives across every event in the list.
  inspectionsLoaded?: boolean;
  // `true` cuando el mapa de arriba es TODO lo que el filtro abarca. Con
  // `false`, no encontrar una inspección no prueba que falte (issue #507): ver
  // inspectionLink.ts. Por defecto `false` —"no sé"— porque el modo de falla
  // caro es afirmar de más.
  inspectionsComplete?: boolean;
  // null = data not loaded yet — skip the check rather than flag it.
  isVigilanteOnDuty?: ((event: YardAccessEvent) => boolean) | null;
  // Water types inherited by each vehicle from its groups (union). Used for
  // the yard-compatibility check; absent → skip the check.
  vehicleInheritedTypes?: Map<string, string[]> | null;
  yardsById?: Map<string, TruckYard> | null;
}

// Vehicle's effective water types: the per-vehicle stored subset intersected
// against the current inherited pool. Mirrors the rule the vehicles section
// uses, so a type dropped from a group disappears here too. If the
// intersection collapses to empty we fall back to the inherited list so the
// "never empty" invariant holds.
function effectiveVehicleTypes(
  vehicle: TruckYardVehicle | null,
  inherited: string[],
): string[] {
  if (!vehicle) return inherited;
  // The per-vehicle subset now lives in `truck_yard_types` (it used to share
  // the unit `type` field). Fall back to the inherited pool when unset.
  if (!Array.isArray(vehicle.truck_yard_types)) return inherited;
  const filtered = vehicle.truck_yard_types.filter((t) =>
    inherited.includes(t),
  );
  return filtered.length > 0 ? filtered : inherited;
}

function deriveAlerts(
  event: YardAccessEvent,
  ctx: DeriveAlertsContext,
  t: TFunction,
): EventAlert[] {
  const alerts: EventAlert[] = [];

  // Qué checks le tocan a este evento lo decide `specialTagCheckMode` (puro,
  // con su test). El tag de proveedores no calcula NINGUNO —ni siquiera
  // "manual" o "sin vigilante"—: devolver la lista vacía acá es lo que deja la
  // tarjeta sin borde de alerta y al evento fuera de las píldoras de conteo.
  const specialMode = specialTagCheckMode(event);
  if (specialMode === "provider_exempt") return alerts;

  if (event.source === "manual") {
    alerts.push({
      key: "manual",
      level: "red",
      message: t("access.alerts.manual"),
      icon: "ri-hand-coin-line",
    });
  }

  // Vehicle assignment
  if (specialMode === "untagged_pending") {
    // Amarillo y no rojo: no es un incidente, es un paso que falta, y dice en
    // qué pantalla se resuelve. "Tag leído sin vehículo asociado" sería cierto
    // pero mandaría a buscar la unidad a un catálogo donde el tag compartido
    // no va a estar nunca.
    alerts.push({
      key: "untagged_pending",
      level: "yellow",
      message: t("access.alerts.untaggedPending"),
      icon: "ri-truck-line",
    });
  } else if (!event.card_number && !event.vehicle_id) {
    alerts.push({
      key: "no_identifier",
      level: "red",
      message: t("access.alerts.noIdentifier"),
      icon: "ri-truck-line",
    });
  } else if (!event.vehicle_id) {
    alerts.push({
      key: "vehicle_not_found",
      level: "yellow",
      message: t("access.alerts.tagNoVehicle"),
      icon: "ri-truck-line",
    });
  } else {
    const vehicle = ctx.vehiclesById.get(event.vehicle_id);
    if (!vehicle) {
      alerts.push({
        key: "vehicle_not_found",
        level: "yellow",
        message: t("access.alerts.vehicleNotRegistered"),
        icon: "ri-truck-line",
      });
    } else {
      const assignedYards = vehicle.yard_ids || [];
      if (assignedYards.length > 0 && !assignedYards.includes(event.yard_id)) {
        alerts.push({
          key: "yard_mismatch",
          level: "yellow",
          message: t("access.alerts.yardMismatch"),
          icon: "ri-building-4-line",
        });
      }

      // Type compatibility: the vehicle's effective water types (group
      // inheritance ∩ per-vehicle subset) must overlap the yard's accepted
      // types. We only flag when both sides declare types — a yard with no
      // type config accepts everything by default, and a vehicle with no
      // inherited types has no claim to enforce.
      const inherited = ctx.vehicleInheritedTypes?.get(vehicle.id) || [];
      const vehicleTypes = effectiveVehicleTypes(vehicle, inherited);
      const yard = ctx.yardsById?.get(event.yard_id);
      const yardTypes = yard?.type || [];
      if (vehicleTypes.length > 0 && yardTypes.length > 0) {
        const overlap = vehicleTypes.some((t) => yardTypes.includes(t));
        if (!overlap) {
          alerts.push({
            key: "vehicle_type_mismatch",
            level: "red",
            message: t("access.alerts.vehicleTypeMismatch"),
            icon: "ri-drop-line",
          });
        }
      }
    }
  }

  // Inspection assignment. Las tres salidas visibles las decide
  // `resolveInspectionLink`, que es donde está explicado por qué "no la
  // encuentro" y "no la traje" tienen que ser dos cosas distintas (issue #507).
  const inspectionLink = resolveInspectionLink({
    inspectionId: event.inspection_id,
    loaded: !!ctx.inspectionsLoaded,
    known: ctx.inspectionsById,
    complete: !!ctx.inspectionsComplete,
  });
  if (inspectionLink === "absent") {
    alerts.push({
      key: "no_inspection",
      level: "red",
      message: t("access.alerts.noInspection"),
      icon: "ri-file-search-line",
    });
  } else if (inspectionLink === "not_found") {
    alerts.push({
      key: "inspection_unknown",
      level: "yellow",
      message: t("access.alerts.inspectionUnknown"),
      icon: "ri-file-search-line",
    });
  } else if (inspectionLink === "unverified") {
    // Se avisa igual, pero sin acusar. Silenciarlo tampoco sirve: la fila
    // "Inspección vinculada" de la tarjeta se dibuja en ámbar de todos modos
    // (no hay inspección que nombrar) y sin este mensaje mostraría el id crudo,
    // que el operador no puede interpretar.
    alerts.push({
      key: "inspection_unverified",
      level: "yellow",
      message: t("access.alerts.inspectionUnverified"),
      icon: "ri-question-line",
    });
  }

  // Vigilante on duty
  if (ctx.isVigilanteOnDuty && !ctx.isVigilanteOnDuty(event)) {
    alerts.push({
      key: "no_vigilante",
      level: "red",
      message: t("access.alerts.noVigilante"),
      icon: "ri-shield-user-line",
    });
  }

  // Exit-only: inherits entry from its linked inspection (created at entry).
  // Only fires once inspections have loaded — otherwise the "no_inspection" /
  // "inspection_unknown" alerts already cover the gap.
  if (event.event_type === "exit" && ctx.inspectionsLoaded) {
    const insp =
      event.inspection_id && ctx.inspectionsById
        ? ctx.inspectionsById.get(event.inspection_id) || null
        : null;
    if (insp) {
      if (!insp.access_entry_id) {
        alerts.push({
          key: "exit_no_entry",
          level: "red",
          message: t("access.alerts.exitNoEntry"),
          icon: "ri-login-box-line",
        });
      } else if (!insp.entry_timestamp) {
        alerts.push({
          key: "exit_no_entry_ts",
          level: "yellow",
          message: t("access.alerts.exitNoEntryTs"),
          icon: "ri-time-line",
        });
      }
    }
  }

  return alerts;
}

function getMaxAlertLevel(alerts: EventAlert[]): AlertLevel {
  if (alerts.some((a) => a.level === "red")) return "red";
  if (alerts.some((a) => a.level === "yellow")) return "yellow";
  return "none";
}

// Background tint scales with the number of unresolved checks. Two alerts is
// the first level above "border-only"; intensity caps at ~40% so the card
// stays legible regardless of theme (pills/buttons opt out of the tint).
function getAlertTintAlpha(count: number): number {
  if (count <= 1) return 0;
  return Math.min(0.4, (count - 1) * 0.1);
}

// ─── On-duty vigilantes ──────────────────────────────────────────────────────
//
// A vigilante is "on duty" for an event when:
//   1. They're active.
//   2. Their assigned yard (yard_id, or yard_ids if present) matches the event's yard.
//   3. Their weekly schedule for the event's day-of-week is enabled AND the
//      event time falls within [from, to] — accounting for overnight shifts
//      where `to` < `from` (e.g., 22:00–06:00 wraps into the next day).
//
// Day-of-week and HH:MM are computed in the yard's configured timezone so an
// event recorded at 23:00 local in CDMX matches a Mexico-City schedule even
// when the operator is viewing from a different TZ.

type DayKey =
  | "lunes"
  | "martes"
  | "miercoles"
  | "jueves"
  | "viernes"
  | "sabado"
  | "domingo";

interface GuardDay {
  enabled?: boolean;
  from?: string;
  to?: string;
}

const DAY_KEYS: DayKey[] = [
  "domingo",
  "lunes",
  "martes",
  "miercoles",
  "jueves",
  "viernes",
  "sabado",
];

function toMinutes(hhmm: string | undefined): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min;
}

function getYardZonedParts(
  date: Date,
  timeZone: string | undefined,
): { weekday: number; minutes: number } {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || undefined,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    const parts = fmt.formatToParts(date);
    const weekdayStr = parts.find((p) => p.type === "weekday")?.value || "";
    const hourStr = parts.find((p) => p.type === "hour")?.value || "0";
    const minStr = parts.find((p) => p.type === "minute")?.value || "0";
    const weekdayMap: Record<string, number> = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };
    const weekday = weekdayMap[weekdayStr] ?? date.getDay();
    const h = Number(hourStr === "24" ? "0" : hourStr);
    return { weekday, minutes: h * 60 + Number(minStr) };
  } catch {
    return { weekday: date.getDay(), minutes: date.getHours() * 60 + date.getMinutes() };
  }
}

function isGuardOnDuty(
  guard: TruckYardUser,
  eventDate: Date,
  yardId: string,
  yardTimeZone: string | undefined,
): boolean {
  const status = guard.status || guard.status_id;
  if (status && status !== "active") return false;

  const guardYardIds = guard.yard_ids && guard.yard_ids.length > 0
    ? guard.yard_ids
    : guard.yard_id
      ? [guard.yard_id]
      : [];
  if (!guardYardIds.includes(yardId)) return false;

  const schedule = guard.schedule as Partial<Record<DayKey, GuardDay>> | undefined;
  if (!schedule) return false;

  const { weekday, minutes: evMinutes } = getYardZonedParts(eventDate, yardTimeZone);
  const todayKey = DAY_KEYS[weekday];
  const today = schedule[todayKey];

  if (today?.enabled) {
    const from = toMinutes(today.from);
    const to = toMinutes(today.to);
    if (from !== null && to !== null) {
      if (from <= to) {
        if (evMinutes >= from && evMinutes <= to) return true;
      } else {
        // Overnight: starts today, ends tomorrow.
        if (evMinutes >= from) return true;
      }
    }
  }

  // Overnight tail: a shift that started yesterday may extend into today.
  const yesterdayKey = DAY_KEYS[(weekday + 6) % 7];
  const yesterday = schedule[yesterdayKey];
  if (yesterday?.enabled) {
    const from = toMinutes(yesterday.from);
    const to = toMinutes(yesterday.to);
    if (from !== null && to !== null && from > to && evMinutes <= to) {
      return true;
    }
  }

  return false;
}

function guardDisplayName(g: TruckYardUser): string {
  const first = g.firstName || g.first_name || "";
  const last = g.lastName || g.last_name || "";
  const full = `${first} ${last}`.trim();
  return full || g.email || g.id;
}

function alertChipClasses(level: AlertLevel): string {
  if (level === "red") return "bg-red-500/15 text-red-400 border border-red-500/40";
  if (level === "yellow") return "bg-yellow-500/15 text-yellow-400 border border-yellow-500/40";
  return "";
}

function alertIcon(level: AlertLevel): string {
  if (level === "red") return "ri-alert-fill";
  if (level === "yellow") return "ri-error-warning-line";
  return "";
}

// ─── Header ──────────────────────────────────────────────────────────────────

interface SelectOption {
  value: string;
  label: string;
}

function AccessHeader({
  themeClasses,
  isDarkMode,
  selectedYardIds,
  selectedGroup,
  selectedVehicle,
  selectedDevice,
  accessType,
  dateFrom,
  dateTo,
  yardOptions,
  yardLocked,
  allowAllYards,
  groupOptions,
  vehicleOptions,
  deviceOptions,
  selectedView,
  alertFilter,
  onYardChange,
  onGroupChange,
  onVehicleChange,
  onDeviceChange,
  onAccessTypeChange,
  onDateFromChange,
  onDateToChange,
  onClearFilters,
  onViewChange,
  onAlertFilterChange,
  onAdd,
  onSync,
  actionsDisabled,
  actionsDisabledReason,
  total,
  loading,
  alertCounts,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  /** Patios elegidos; `[]` = todos (sólo si `allowAllYards`). */
  selectedYardIds: string[];
  selectedGroup: string;
  selectedVehicle: string;
  selectedDevice: string;
  accessType: AccessTypeFilter | null;
  dateFrom: string;
  dateTo: string;
  yardOptions: SelectOption[];
  /** Un solo patio posible: el combo se dibuja fijo, con el nombre a la vista. */
  yardLocked: boolean;
  /**
   * Si "todos los patios" es una opción. Para el inspector y el vigilante NO lo
   * es (issue #496), y entonces el combo tampoco se puede limpiar: vaciarlo
   * significaría pedir la empresa entera.
   */
  allowAllYards: boolean;
  groupOptions: SelectOption[];
  vehicleOptions: SelectOption[];
  deviceOptions: SelectOption[];
  selectedView: AccessView;
  alertFilter: AlertLevel | null;
  onYardChange: (v: string[]) => void;
  onGroupChange: (v: string) => void;
  onVehicleChange: (v: string) => void;
  onDeviceChange: (v: string) => void;
  onAccessTypeChange: (v: AccessTypeFilter | null) => void;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onClearFilters: () => void;
  onViewChange: (v: AccessView) => void;
  onAlertFilterChange: (v: AlertLevel | null) => void;
  onAdd: () => void;
  onSync: () => void;
  /** Sin patios asignados no hay patio válido bajo el cual dar de alta ni con
   *  qué sincronizar: los dos botones se deshabilitan de verdad. */
  actionsDisabled: boolean;
  /** El motivo, visible en el `title`: un botón inerte y mudo se lee como que
   *  la pantalla está rota. */
  actionsDisabledReason: string;
  total: number;
  loading: boolean;
  alertCounts: { red: number; yellow: number };
}) {
  const { t } = useTranslation("truckYard");
  const selectStyles = getCustomSelectStyles(isDarkMode);
  const dateInputCls = `px-3 py-2 ${
    isDarkMode ? "bg-dark-card text-white" : "bg-gray-100 text-gray-900"
  } border ${themeClasses.cardBorder} rounded-lg outline-none text-sm focus:ring-2 focus:ring-primary-500/30`;

  const hasActiveFilters =
    // Sólo cuenta como filtro si se podía NO tenerlo: para un vigilante el patio
    // viene forzado por su alcance y "Limpiar filtros" no lo puede deshacer.
    //
    // Hoy no cambia lo que se ve, y conviene saberlo antes de tocarlo: el rango
    // arranca en el día en curso, así que `!!dateFrom || !!dateTo` ya deja la
    // bandera encendida en todo estado alcanzable por defecto. Sólo se nota si
    // el operador vacía los dos inputs de fecha a mano, que es el único camino
    // por el que esta bandera puede llegar a valer false.
    (allowAllYards && selectedYardIds.length > 0) ||
    !!selectedGroup ||
    !!selectedVehicle ||
    !!selectedDevice ||
    !!accessType ||
    !!dateFrom ||
    !!dateTo ||
    !!alertFilter;

  const groupValue = groupOptions.find((o) => o.value === selectedGroup) || null;
  const vehicleValue = vehicleOptions.find((o) => o.value === selectedVehicle) || null;
  const deviceValue = deviceOptions.find((o) => o.value === selectedDevice) || null;

  // Grouped so "Entrada / Salida" (movement) reads apart from
  // "Autorizado / Denegado / Desconocido" (credential decision).
  const accessTypeGroups = [
    {
      label: t("access.filters.accessTypeMovement"),
      options: [
        { value: "entry" as const, label: t("access.eventType.entry") },
        { value: "exit" as const, label: t("access.eventType.exit") },
      ],
    },
    {
      label: t("access.filters.accessTypeResult"),
      options: [
        { value: "granted" as const, label: t("access.result.granted") },
        { value: "denied" as const, label: t("access.result.denied") },
        { value: "unknown" as const, label: t("access.result.unknown") },
      ],
    },
  ];
  const accessTypeValue =
    accessTypeGroups.flatMap<{ value: string; label: string }>((g) => g.options).find((o) => o.value === accessType) || null;

  return (
    <div className={`${themeClasses.bg} border-b ${themeClasses.border} p-4 sm:p-6`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <p className={`text-xs ${themeClasses.textSecondary} mb-0.5`}>{t("common.breadcrumb")}</p>
          <h1 className={`text-xl font-bold ${themeClasses.text} flex flex-wrap items-center gap-2`}>
            {t("access.title")}
            {!loading && (
              <span className={`text-sm font-normal ${themeClasses.textSecondary}`}>({total})</span>
            )}
            {alertCounts.red > 0 && (
              <button
                type="button"
                onClick={() => onAlertFilterChange(alertFilter === "red" ? null : "red")}
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${alertChipClasses("red")} flex items-center gap-1 cursor-pointer transition-all hover:brightness-110 ${
                  alertFilter === "red"
                    ? "ring-2 ring-red-500/70 ring-offset-2 ring-offset-transparent"
                    : ""
                }`}
                title={
                  alertFilter === "red"
                    ? t("access.filterCriticalOn")
                    : t("access.filterCriticalOff")
                }
              >
                <i className={`${alertIcon("red")} text-sm`}></i>
                {alertCounts.red}
              </button>
            )}
            {alertCounts.yellow > 0 && (
              <button
                type="button"
                onClick={() => onAlertFilterChange(alertFilter === "yellow" ? null : "yellow")}
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${alertChipClasses("yellow")} flex items-center gap-1 cursor-pointer transition-all hover:brightness-110 ${
                  alertFilter === "yellow"
                    ? "ring-2 ring-yellow-500/70 ring-offset-2 ring-offset-transparent"
                    : ""
                }`}
                title={
                  alertFilter === "yellow"
                    ? t("access.filterWarningsOn")
                    : t("access.filterWarningsOff")
                }
              >
                <i className={`${alertIcon("yellow")} text-sm`}></i>
                {alertCounts.yellow}
              </button>
            )}
          </h1>
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {t("access.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <div className={`flex ${isDarkMode ? "bg-dark-card" : "bg-gray-100"} rounded-lg p-1 gap-1`}>
            <button
              onClick={() => onViewChange("grid")}
              className={`p-1.5 rounded-md transition-colors ${
                selectedView === "grid"
                  ? isDarkMode ? "bg-dark-surface text-white" : "bg-white text-gray-900 shadow-sm"
                  : themeClasses.buttonText
              }`}
              title={t("common.gridView")}
            >
              <i className="ri-grid-line text-lg"></i>
            </button>
            <button
              onClick={() => onViewChange("list")}
              className={`p-1.5 rounded-md transition-colors ${
                selectedView === "list"
                  ? isDarkMode ? "bg-dark-surface text-white" : "bg-white text-gray-900 shadow-sm"
                  : themeClasses.buttonText
              }`}
              title={t("common.listView")}
            >
              <i className="ri-list-unordered text-lg"></i>
            </button>
            <button
              onClick={() => onViewChange("table")}
              className={`p-1.5 rounded-md transition-colors ${
                selectedView === "table"
                  ? isDarkMode ? "bg-dark-surface text-white" : "bg-white text-gray-900 shadow-sm"
                  : themeClasses.buttonText
              }`}
              title={t("common.tableView")}
            >
              <i className="ri-table-line text-lg"></i>
            </button>
          </div>
          {/* `disabled` de verdad, no un handler que no hace nada. Los dos
              botones abrían un modal cuyo `<select>` de patio quedaba sin
              opciones, así que el click era un no-op silencioso: el usuario
              vuelve a apretar y concluye que la pantalla está rota. El motivo va
              en el `title` porque es la única superficie que le queda a un
              botón deshabilitado. */}
          <button
            onClick={onSync}
            disabled={actionsDisabled}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm transition-colors border ${themeClasses.cardBorder} ${
              actionsDisabled
                ? "opacity-50 cursor-not-allowed " +
                  (isDarkMode
                    ? "bg-dark-card text-white"
                    : "bg-gray-100 text-gray-900")
                : isDarkMode
                  ? "bg-dark-card text-white hover:bg-dark-surface"
                  : "bg-gray-100 text-gray-900 hover:bg-gray-200"
            }`}
            title={
              actionsDisabled ? actionsDisabledReason : t("access.syncButton")
            }
          >
            <i className="ri-refresh-line"></i>
            <span className="hidden sm:inline">{t("access.syncButton")}</span>
            <span className="sm:hidden">{t("access.syncButtonShort")}</span>
          </button>
          <button
            onClick={onAdd}
            disabled={actionsDisabled}
            className={`text-gray-900 px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm transition-colors ${
              actionsDisabled
                ? "bg-primary-500 opacity-50 cursor-not-allowed"
                : "bg-primary-500 hover:bg-primary-400"
            }`}
            title={
              actionsDisabled ? actionsDisabledReason : t("access.addButton")
            }
          >
            <i className="ri-add-line"></i>
            <span className="hidden sm:inline">{t("access.addButton")}</span>
            <span className="sm:hidden">{t("access.addButtonShort")}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-3 items-end">
        <div>
          <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
            {t("access.filters.yard")}
          </label>
          {/* Selección múltiple: se pueden elegir varios patios a la vez. Sin
              ninguno elegido es "todos" — y eso sólo existe para quien puede
              ver el agregado (#496); para el inspector y el vigilante el combo
              no se limpia y, si lo vacían, `resolveMultiYardScopedRequest`
              vuelve a su patio en el mismo render. */}
          <MultiSelectFilter
            isDarkMode={isDarkMode}
            options={yardOptions}
            values={selectedYardIds}
            onChange={onYardChange}
            // El placeholder es lo que se lee cuando no hay valor, y decía
            // "Todos los patios" para todos los roles. Al inspector y al
            // vigilante eso les describía mal su propia pantalla — nunca están
            // viendo todos los patios — y era la parte visible del defecto de
            // #496. Sin la opción "todos", el combo tampoco se limpia.
            placeholder={
              allowAllYards
                ? t("access.filters.allYards")
                : t("access.filters.yard")
            }
            isClearable={allowAllYards}
            isDisabled={yardLocked}
            noOptionsMessage={t("access.filters.noYards")}
          />
        </div>
        <div>
          <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
            {t("access.filters.group")}
          </label>
          <Select
            options={groupOptions}
            value={groupValue}
            onChange={(o) => onGroupChange((o as SelectOption | null)?.value || "")}
            styles={selectStyles}
            placeholder={t("access.filters.allGroups")}
            isClearable
            isSearchable
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
          />
        </div>
        <div>
          <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
            {t("access.filters.vehicle")}
          </label>
          <Select
            options={vehicleOptions}
            value={vehicleValue}
            onChange={(o) => onVehicleChange((o as SelectOption | null)?.value || "")}
            styles={selectStyles}
            placeholder={t("access.filters.allVehicles")}
            isClearable
            isSearchable
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
          />
        </div>
        <div>
          <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
            {t("access.filters.device")}
          </label>
          <Select
            options={deviceOptions}
            value={deviceValue}
            onChange={(o) => onDeviceChange((o as SelectOption | null)?.value || "")}
            styles={selectStyles}
            placeholder={t("access.filters.allDevices")}
            isClearable
            isSearchable
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
            noOptionsMessage={() =>
              selectedYardIds.length > 0
                ? t("access.filters.noDevicesInYard")
                : t("access.filters.noDevices")
            }
          />
        </div>
        <div>
          <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
            {t("access.filters.accessType")}
          </label>
          <Select
            options={accessTypeGroups}
            value={accessTypeValue}
            onChange={(o) =>
              onAccessTypeChange(
                (o as { value: AccessTypeFilter } | null)?.value ?? null,
              )
            }
            styles={selectStyles}
            placeholder={t("access.filters.allAccessTypes")}
            isClearable
            isSearchable
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
          />
        </div>
        <div>
          <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
            {t("access.filters.from")}
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className={`${dateInputCls} w-full`}
          />
        </div>
        <div>
          <label className={`text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium block mb-1`}>
            {t("access.filters.to")}
          </label>
          <div className="flex gap-2">
            <input
              type="date"
              value={dateTo}
              onChange={(e) => onDateToChange(e.target.value)}
              className={`${dateInputCls} w-full`}
            />
            {hasActiveFilters && (
              <button
                type="button"
                onClick={onClearFilters}
                title={t("access.filters.clearFilters")}
                className={`px-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.buttonText} ${themeClasses.buttonHover} transition-colors`}
              >
                <i className="ri-filter-off-line text-base"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Event Grid Card ─────────────────────────────────────────────────────────

function EventGridCard({
  event,
  alerts,
  vehicle,
  linkedInspection,
  onDutyGuards,
  vehicleTypes,
  yardTypes,
  themeClasses,
  isDarkMode,
  onDetails,
}: {
  event: YardAccessEvent;
  alerts: EventAlert[];
  vehicle: TruckYardVehicle | null;
  linkedInspection: YardInspection | null;
  onDutyGuards: TruckYardUser[];
  vehicleTypes: string[];
  yardTypes: string[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (e: YardAccessEvent) => void;
}) {
  const { t } = useTranslation("truckYard");
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const isEntry = event.event_type === "entry";
  const maxLevel = getMaxAlertLevel(alerts);
  const alertBorderClass =
    maxLevel === "red"
      ? "border-red-500/50 ring-1 ring-red-500/40"
      : maxLevel === "yellow"
        ? "border-yellow-500/50 ring-1 ring-yellow-500/30"
        : themeClasses.cardBorder;

  const tintAlpha = getAlertTintAlpha(alerts.length);
  const tintStyle =
    tintAlpha > 0
      ? { backgroundColor: `rgba(239, 68, 68, ${tintAlpha})` }
      : undefined;

  const displayName = vehicle?.name || event.vehicle_plate || event.card_number || "—";
  const displaySub = vehicle
    ? [vehicle.plate, vehicle.brand, vehicle.model].filter(Boolean).join(" · ") || event.driver_name || t("access.card.noDriver")
    : event.driver_name || t("access.card.noDriver");

  return (
    <div
      className={`${tintStyle ? "" : themeClasses.cardBg} border ${alertBorderClass} rounded-2xl p-5 hover:shadow-lg transition-all duration-200`}
      style={tintStyle}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isEntry ? "bg-green-500/20" : "bg-red-500/20"}`}>
            <i className={`${isEntry ? "ri-login-box-line text-green-500" : "ri-logout-box-line text-red-500"} text-xl`}></i>
          </div>
          <div className="min-w-0">
            <p className={`font-semibold text-sm ${themeClasses.text} truncate`}>{displayName}</p>
            <span className={`text-xs ${themeClasses.textSecondary}`}>{displaySub}</span>
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${resultClasses(event.event_result)}`}>
          {resultLabel(event.event_result, t)}
        </span>
      </div>

      <div className={`${surface} rounded-xl p-3 space-y-2 mb-3`}>
        <div className="flex items-center gap-2">
          <i className={`ri-building-4-line text-xs ${themeClasses.textSecondary}`}></i>
          <span className={`text-xs ${themeClasses.textSecondary} truncate`}>{event.yard_name || "—"}</span>
        </div>
        <div className="flex items-center gap-2">
          <i className={`ri-time-line text-xs ${themeClasses.textSecondary}`}></i>
          <span className={`text-xs ${themeClasses.textSecondary}`}>{formatTimestamp(event.timestamp)}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full ${eventTypeClasses(event.event_type)}`}>
              {eventTypeLabel(event.event_type, t)}
            </span>
            <SuspiciousBadge event={event} />
            <EventSpecialTagBadge event={event} />
          </div>
          <span className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium`}>
            {sourceLabel(event.source, t)}
          </span>
        </div>
      </div>

      <EventChecksList
        event={event}
        alerts={alerts}
        vehicle={vehicle}
        linkedInspection={linkedInspection}
        onDutyGuards={onDutyGuards}
        vehicleTypes={vehicleTypes}
        yardTypes={yardTypes}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        className="mb-4"
      />

      <button
        onClick={() => onDetails(event)}
        className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-all"
        style={{ backgroundColor: brand.primary, color: brand.accent }}
      >
        <i className="ri-eye-line text-sm"></i>
        {t("access.card.details")}
      </button>
    </div>
  );
}

// ─── Event Checks (per-variable rows) ────────────────────────────────────────
//
// Each row corresponds to one of the alert variables (vehicle, inspection,
// vigilante, linked entry). When the variable resolves we render its value;
// when it doesn't we fall back to the matching alert message in red/yellow.

type CheckRowState =
  | { state: "ok"; label: string }
  | { state: "warn"; label: string }
  | { state: "missing"; label: string };

function CheckRow({
  icon,
  state,
  themeClasses,
  isDarkMode,
}: {
  icon: string;
  state: CheckRowState;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
}) {
  const isOk = state.state === "ok";
  const isWarn = state.state === "warn";
  const bg = isOk
    ? isDarkMode
      ? "bg-emerald-500/10"
      : "bg-emerald-50"
    : isWarn
      ? isDarkMode
        ? "bg-yellow-500/10"
        : "bg-yellow-50"
      : isDarkMode
        ? "bg-red-500/10"
        : "bg-red-50";
  const iconColor = isOk
    ? "text-emerald-400"
    : isWarn
      ? "text-yellow-400"
      : "text-red-400";
  const textColor = isOk
    ? themeClasses.text
    : isWarn
      ? isDarkMode
        ? "text-yellow-300"
        : "text-yellow-700"
      : isDarkMode
        ? "text-red-300"
        : "text-red-700";
  const statusIcon = isOk
    ? "ri-check-line"
    : isWarn
      ? "ri-alert-line"
      : "ri-close-circle-line";
  return (
    <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${bg}`}>
      <i className={`${icon} text-xs ${iconColor}`}></i>
      <span className={`text-[11px] font-medium truncate flex-1 ${textColor}`}>
        {state.label}
      </span>
      <i className={`${statusIcon} text-xs ${iconColor}`}></i>
    </div>
  );
}

function EventChecksList({
  event,
  alerts,
  vehicle,
  linkedInspection,
  onDutyGuards,
  vehicleTypes,
  yardTypes,
  themeClasses,
  isDarkMode,
  className,
}: {
  event: YardAccessEvent;
  alerts: EventAlert[];
  vehicle: TruckYardVehicle | null;
  linkedInspection: YardInspection | null;
  onDutyGuards: TruckYardUser[];
  vehicleTypes: string[];
  yardTypes: string[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  className?: string;
}) {
  const { t } = useTranslation("truckYard");
  const alertByKey = new Map(alerts.map((a) => [a.key, a]));

  // Tag de proveedores: una sola línea informativa en lugar de las filas. Sin
  // este corte las filas se dibujarían igual —se arman con lo que el evento
  // resuelve, no con `alerts`— y un proveedor mostraría "Sin inspección
  // asociada" y "Sin vigilante en turno" en rojo, que es justo lo que el tag
  // especial vino a dejar de decir.
  if (specialTagCheckMode(event) === "provider_exempt") {
    return (
      <div className={`space-y-1.5 ${className || ""}`}>
        <div
          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${
            isDarkMode ? "bg-purple-500/10" : "bg-purple-50"
          }`}
        >
          <i className="ri-information-line text-xs text-purple-400"></i>
          <span
            className={`text-[11px] font-medium flex-1 ${
              isDarkMode ? "text-purple-300" : "text-purple-700"
            }`}
          >
            {t("access.specialTag.providerNoChecks")}
          </span>
        </div>
      </div>
    );
  }

  // Vehicle row: prefers the catalog match, otherwise echoes whatever the
  // event managed to capture so the operator still sees the raw identifier.
  const vehicleState: CheckRowState = vehicle
    ? {
        state: "ok",
        label:
          [vehicle.name, vehicle.plate].filter(Boolean).join(" · ") || vehicle.id,
      }
    : alertByKey.get("untagged_pending")
      ? // El número del tag compartido no identifica a nadie: en vez de
        // repetirlo, la fila dice qué falta y dónde se captura.
        { state: "warn", label: alertByKey.get("untagged_pending")!.message }
    : alertByKey.get("no_identifier")
      ? { state: "missing", label: alertByKey.get("no_identifier")!.message }
      : {
          state: "warn",
          label:
            event.vehicle_plate ||
            event.card_number ||
            alertByKey.get("vehicle_not_found")?.message ||
            t("access.card.noVehicleRegistered"),
        };

  const inspectionState: CheckRowState = linkedInspection
    ? {
        state: "ok",
        label: linkedInspection.title || t("access.inspection", { id: linkedInspection.id }),
      }
    : event.inspection_id
      ? {
          state: "warn",
          // Las dos claves, porque son dos hechos distintos y sólo una está
          // presente a la vez (issue #507): "no encontrada" cuando el conjunto
          // cargado es completo, "no verificable" cuando vino truncado. Sin la
          // segunda esta fila caía al `|| event.inspection_id` y le mostraba al
          // operador un id de Firestore.
          label:
            alertByKey.get("inspection_unknown")?.message ||
            alertByKey.get("inspection_unverified")?.message ||
            event.inspection_id,
        }
      : { state: "missing", label: t("access.card.noInspection") };

  const vigilanteState: CheckRowState =
    onDutyGuards.length > 0
      ? {
          state: "ok",
          label:
            onDutyGuards.length === 1
              ? guardDisplayName(onDutyGuards[0])
              : t("access.card.vigilantesOnDuty", { count: onDutyGuards.length }),
        }
      : { state: "missing", label: t("access.card.noVigilante") };

  // Type compatibility row: shown only when both sides declare types — a
  // yard without a type config accepts everything, and a vehicle with no
  // group-inherited types has no claim to check against.
  const showTypeRow = vehicleTypes.length > 0 && yardTypes.length > 0;
  const matchedTypes = showTypeRow
    ? vehicleTypes.filter((vt) => yardTypes.includes(vt))
    : [];
  const typeState: CheckRowState | null = showTypeRow
    ? matchedTypes.length > 0
      ? {
          state: "ok",
          label: matchedTypes.map(getWaterTypeLabel).join(", "),
        }
      : { state: "missing", label: t("access.card.typeNotAccepted") }
    : null;

  // The other half of the visit. Both directions are shown: an exit card names
  // the entry it closes, and an entry card names the exit that closed it (or
  // says the vehicle is still inside). Showing it only on exits left every
  // entry card looking unpaired even when its visit was complete.
  const isEntryEvent = event.event_type === "entry";
  const counterpartTs = isEntryEvent
    ? linkedInspection?.exit_timestamp
    : linkedInspection?.entry_timestamp;
  const counterpartId = isEntryEvent
    ? linkedInspection?.access_exit_id
    : linkedInspection?.access_entry_id;

  const counterpartState: CheckRowState = counterpartTs
    ? {
        state: "ok",
        label: isEntryEvent
          ? t("access.card.exitTimestamp", { time: formatTimestamp(counterpartTs) })
          : t("access.card.entryTimestamp", { time: formatTimestamp(counterpartTs) }),
      }
    : counterpartId
      ? { state: "warn", label: isEntryEvent ? t("access.card.exitNoTs") : t("access.card.entryNoTs") }
      // An entry with no exit yet is the normal state of a vehicle inside the
      // yard, not a defect — so it reads as pending, while an exit with no
      // entry really is a gap in the record.
      : isEntryEvent
        ? { state: "warn", label: t("access.card.stillInside") }
        : { state: "missing", label: t("access.card.noEntryLinked") };

  // Catch-all for alerts that aren't owned by a specific row (e.g., manual,
  // yard_mismatch). Rendered as compact chips above the rows.
  const SIDE_BAND_KEYS = new Set<AlertReason>(["manual", "yard_mismatch"]);
  const sideAlerts = alerts.filter((a) => SIDE_BAND_KEYS.has(a.key));

  return (
    <div className={`space-y-1.5 ${className || ""}`}>
      {sideAlerts.map((a) => (
        <div
          key={a.key}
          className={`flex items-center gap-2 px-2.5 py-1 rounded-lg ${alertChipClasses(a.level)}`}
        >
          <i className={`${a.icon} text-xs`}></i>
          <span className="text-[11px] font-medium truncate">{a.message}</span>
        </div>
      ))}

      <CheckRow
        icon="ri-truck-line"
        state={vehicleState}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
      />
      <CheckRow
        icon="ri-file-search-line"
        state={inspectionState}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
      />
      <CheckRow
        icon="ri-shield-user-line"
        state={vigilanteState}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
      />
      {typeState && (
        <CheckRow
          icon="ri-drop-line"
          state={typeState}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
        />
      )}
      <CheckRow
        icon={isEntryEvent ? "ri-logout-box-line" : "ri-login-box-line"}
        state={counterpartState}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}

// ─── Event List Row ──────────────────────────────────────────────────────────

function EventListRow({
  event,
  alerts,
  vehicle,
  linkedInspection,
  onDutyGuards,
  vehicleTypes,
  yardTypes,
  themeClasses,
  isDarkMode,
  onDetails,
}: {
  event: YardAccessEvent;
  alerts: EventAlert[];
  vehicle: TruckYardVehicle | null;
  linkedInspection: YardInspection | null;
  onDutyGuards: TruckYardUser[];
  vehicleTypes: string[];
  yardTypes: string[];
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (e: YardAccessEvent) => void;
}) {
  const { t } = useTranslation("truckYard");
  const isEntry = event.event_type === "entry";
  const maxLevel = getMaxAlertLevel(alerts);
  const alertBorderClass =
    maxLevel === "red"
      ? "border-red-500/50 ring-1 ring-red-500/40"
      : maxLevel === "yellow"
        ? "border-yellow-500/50 ring-1 ring-yellow-500/30"
        : themeClasses.cardBorder;

  const tintAlpha = getAlertTintAlpha(alerts.length);
  const tintStyle =
    tintAlpha > 0
      ? { backgroundColor: `rgba(239, 68, 68, ${tintAlpha})` }
      : undefined;

  const displayName = vehicle?.name || event.vehicle_plate || event.card_number || "—";
  const displaySub = vehicle
    ? [vehicle.plate, vehicle.brand, vehicle.model].filter(Boolean).join(" · ") || event.driver_name || t("access.card.noDriver")
    : event.driver_name || t("access.card.noDriver");

  return (
    <div
      className={`${tintStyle ? "" : themeClasses.cardBg} border ${alertBorderClass} rounded-xl p-4 hover:shadow-md transition-all duration-200`}
      style={tintStyle}
    >
      {/* Mobile */}
      <div className="flex items-center gap-3 sm:hidden">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isEntry ? "bg-green-500/20" : "bg-red-500/20"}`}>
          <i className={`${isEntry ? "ri-login-box-line text-green-500" : "ri-logout-box-line text-red-500"} text-lg`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm ${themeClasses.text} truncate`}>{displayName}</p>
          <p className={`text-xs ${themeClasses.textSecondary}`}>{formatTimestamp(event.timestamp)} · {event.yard_name}</p>
        </div>
        <button onClick={() => onDetails(event)} className="w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0" style={{ backgroundColor: brand.primary }}>
          <i className="ri-eye-line text-white text-sm"></i>
        </button>
      </div>

      {/* Desktop */}
      <div className="hidden sm:flex items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isEntry ? "bg-green-500/20" : "bg-red-500/20"}`}>
            <i className={`${isEntry ? "ri-login-box-line text-green-500" : "ri-logout-box-line text-red-500"} text-lg`}></i>
          </div>
          <div className="min-w-0">
            <p className={`font-semibold text-sm ${themeClasses.text} truncate`}>{displayName}</p>
            <span className={`text-xs ${themeClasses.textSecondary}`}>{displaySub}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="hidden md:block text-center min-w-[120px]">
            <p className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}>{t("access.list.yard")}</p>
            <p className={`text-sm ${themeClasses.text} truncate max-w-[120px]`}>{event.yard_name || "—"}</p>
          </div>
          <div className="hidden lg:block text-center min-w-[130px]">
            <p className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}>{t("access.list.dateTime")}</p>
            <p className={`text-xs ${themeClasses.text}`}>{formatTimestamp(event.timestamp)}</p>
          </div>
          <div className="hidden xl:block text-center min-w-[80px]">
            <p className={`text-[10px] uppercase tracking-wide ${themeClasses.textSecondary}`}>{t("access.list.source")}</p>
            <p className={`text-xs ${themeClasses.text}`}>{sourceLabel(event.source, t)}</p>
          </div>

          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${eventTypeClasses(event.event_type)}`}>
            {eventTypeLabel(event.event_type, t)}
          </span>

          <SuspiciousBadge event={event} />

          <EventSpecialTagBadge event={event} />

          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${resultClasses(event.event_result)}`}>
            {resultLabel(event.event_result, t)}
          </span>

          <button
            onClick={() => onDetails(event)}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0"
            style={{ backgroundColor: brand.primary }}
            title={t("access.card.details")}
          >
            <i className="ri-eye-line text-white text-sm"></i>
          </button>
        </div>
      </div>

      <EventChecksList
        event={event}
        alerts={alerts}
        vehicle={vehicle}
        linkedInspection={linkedInspection}
        onDutyGuards={onDutyGuards}
        vehicleTypes={vehicleTypes}
        yardTypes={yardTypes}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        className="mt-3"
      />
    </div>
  );
}

// ─── Event Details Modal ─────────────────────────────────────────────────────

function EventDetailsModal({
  event,
  alerts,
  vehicle,
  themeClasses,
  isDarkMode,
  vigilantes,
  linkedInspection,
  onClose,
}: {
  event: YardAccessEvent;
  alerts: EventAlert[];
  vehicle: TruckYardVehicle | null;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  vigilantes: TruckYardUser[];
  linkedInspection?: YardInspection | null;
  onClose: () => void;
}) {
  const { t } = useTranslation("truckYard");
  const navigate = useNavigate();
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const labelColor = isDarkMode ? "text-gray-400" : "text-gray-500";
  const isEntry = event.event_type === "entry";

  const headerTitle = vehicle?.name
    ? `${eventTypeLabel(event.event_type, t)} — ${vehicle.name}`
    : `${eventTypeLabel(event.event_type, t)} — ${event.vehicle_plate || event.card_number || "—"}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>{t("access.details.title")}</h2>
          <button onClick={onClose} className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}>
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {alerts.length > 0 && (
            <div
              className={`rounded-xl p-4 ${
                getMaxAlertLevel(alerts) === "red"
                  ? "bg-red-500/10 border border-red-500/30"
                  : "bg-yellow-500/10 border border-yellow-500/30"
              }`}
            >
              <div className="flex items-center gap-2 mb-3">
                <i
                  className={`${alertIcon(getMaxAlertLevel(alerts))} text-base ${
                    getMaxAlertLevel(alerts) === "red"
                      ? "text-red-400"
                      : "text-yellow-400"
                  }`}
                ></i>
                <p
                  className={`text-sm font-semibold ${
                    getMaxAlertLevel(alerts) === "red"
                      ? "text-red-400"
                      : "text-yellow-400"
                  }`}
                >
                  {alerts.length === 1
                    ? t("access.details.alertsUnresolvedOne")
                    : t("access.details.alertsUnresolvedOther", { count: alerts.length })}
                </p>
              </div>
              <ul className="space-y-1.5">
                {alerts.map((a) => (
                  <li
                    key={a.key}
                    className={`flex items-center gap-2 text-xs ${themeClasses.text}`}
                  >
                    <i
                      className={`${a.icon} text-sm ${a.level === "red" ? "text-red-400" : "text-yellow-400"}`}
                    ></i>
                    <span className="flex-1">{a.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Event header */}
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isEntry ? "bg-green-500/20" : "bg-red-500/20"}`}>
              <i className={`${isEntry ? "ri-login-box-line text-green-500" : "ri-logout-box-line text-red-500"} text-2xl`}></i>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={`text-base font-bold ${themeClasses.text}`}>{headerTitle}</h3>
              <span className={`text-xs ${themeClasses.textSecondary}`}>{formatTimestamp(event.timestamp)}</span>
            </div>
            <span className={`text-xs px-2.5 py-1.5 rounded-full font-medium ${resultClasses(event.event_result)}`}>
              {resultLabel(event.event_result, t)}
            </span>
          </div>

          {/* Direction inferred from presence rather than read off the reader */}
          <SuspiciousNotice event={event} />

          {/* Tag especial del patio. Para el de proveedores el recuadro de
              alertas de arriba no existe (no hay checks), y sin esta línea el
              detalle no diría por qué. */}
          {eventSpecialTag(event) && (
            <div className="flex items-center gap-2 flex-wrap">
              <EventSpecialTagBadge event={event} />
              {specialTagCheckMode(event) === "provider_exempt" && (
                <span className={`text-xs ${themeClasses.textSecondary}`}>
                  {t("access.specialTag.providerNoChecks")}
                </span>
              )}
            </div>
          )}

          {/* Vehicle info (from catalog) */}
          {vehicle && (
            <div className={`${surface} rounded-xl p-4 flex items-center gap-3`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary-500/20">
                <i className="ri-truck-line text-primary-500 text-lg"></i>
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-semibold ${themeClasses.text}`}>{vehicle.name}</p>
                <p className={`text-xs ${themeClasses.textSecondary}`}>
                  {[vehicle.plate, vehicle.brand, vehicle.model, vehicle.year].filter(Boolean).join(" · ")}
                </p>
              </div>
            </div>
          )}

          {/* Info grid */}
          <div className={`${surface} rounded-xl p-4 grid grid-cols-2 gap-4`}>
            <div>
              <p className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}>{t("access.details.yard")}</p>
              <p className={`text-sm ${themeClasses.text}`}>{event.yard_name || "—"}</p>
            </div>
            <div>
              <p className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}>{t("access.details.driver")}</p>
              <p className={`text-sm ${themeClasses.text}`}>{event.driver_name || t("access.details.noData")}</p>
            </div>
            <div>
              <p className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}>{t("access.details.plate")}</p>
              <p className={`text-sm font-mono ${themeClasses.text}`}>{vehicle?.plate || event.vehicle_plate || "—"}</p>
            </div>
            <div>
              <p className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}>{t("access.details.card")}</p>
              <p className={`text-sm font-mono ${themeClasses.text}`}>{event.card_number || "—"}</p>
            </div>
            <div>
              <p className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}>{t("access.details.source")}</p>
              <p className={`text-sm ${themeClasses.text}`}>{sourceLabel(event.source, t)}</p>
            </div>
            <div>
              <p className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1`}>{t("access.details.result")}</p>
              <p className={`text-sm ${themeClasses.text}`}>{resultLabel(event.event_result, t)}</p>
            </div>
          </div>

          {/* Vigilante/s en turno */}
          <div className={`${surface} rounded-xl p-4`}>
            <p className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor} mb-3`}>
              <i className="ri-shield-user-line mr-1"></i>
              {vigilantes.length === 1 ? t("access.details.vigilanteOnDuty") : t("access.details.vigilantesOnDutyTitle")}
            </p>
            {vigilantes.length === 0 ? (
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {t("access.details.noVigilantesOnDuty")}
              </p>
            ) : (
              <ul className="space-y-2">
                {vigilantes.map((g) => (
                  <li key={g.id} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-500/20 text-blue-400 font-bold text-xs">
                      {((g.firstName || g.first_name || "")[0] || "") +
                        ((g.lastName || g.last_name || "")[0] || "")}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                        {guardDisplayName(g)}
                      </p>
                      {g.phone && (
                        <p className={`text-xs ${themeClasses.textSecondary}`}>
                          <i className="ri-phone-line mr-1"></i>
                          {g.phone}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Hikvision raw data */}
          {event.hikvision_event && (
            <div className={`${surface} rounded-xl p-4`}>
              <p className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor} mb-3`}>
                <i className="ri-router-line mr-1"></i>{t("access.details.hikvisionData")}
              </p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className={labelColor}>{t("access.details.device")}: </span>
                  <span className={`font-mono ${themeClasses.text}`}>{event.hikvision_event.device_serial || "—"}</span>
                </div>
                <div>
                  <span className={labelColor}>{t("access.details.door")}: </span>
                  <span className={themeClasses.text}>#{event.hikvision_event.door_no}</span>
                </div>
                <div>
                  <span className={labelColor}>{t("access.details.majorMinor")}: </span>
                  <span className={`font-mono ${themeClasses.text}`}>{event.hikvision_event.major}/{event.hikvision_event.minor}</span>
                </div>
                <div>
                  <span className={labelColor}>{t("access.details.verification")}: </span>
                  <span className={themeClasses.text}>{event.hikvision_event.verify_mode || "—"}</span>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {event.notes && (
            <div className={`${surface} rounded-xl p-4`}>
              <p className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor} mb-2`}>{t("access.details.notes")}</p>
              <p className={`text-sm ${themeClasses.text}`}>{event.notes}</p>
            </div>
          )}

          {/* Linked Inspection */}
          {event.inspection_id && (
            <button
              type="button"
              onClick={() => {
                const id = event.inspection_id;
                onClose();
                navigate(`/inspecciones/historial?inspection=${id}`);
              }}
              title={t("access.details.viewInspection")}
              className={`${surface} rounded-xl p-4 flex items-center gap-3 w-full text-left ${themeClasses.buttonHover} hover:ring-1 hover:ring-primary-500/40 transition-all`}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary-500/20 text-primary-500">
                <i className="ri-file-search-line text-lg"></i>
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor}`}>{t("access.details.linkedInspection")}</p>
                <p className={`text-xs font-mono ${themeClasses.text} truncate`}>{event.inspection_id}</p>
              </div>
              <i className={`ri-arrow-right-s-line text-lg ${themeClasses.textSecondary} flex-shrink-0`}></i>
            </button>
          )}

          {/* The other half of the visit, in whichever direction this event
              isn't: an exit links back to its entry, an entry forward to its
              exit. Same visit, so the same inspection carries both ids. */}
          {(() => {
            const otherId = isEntry
              ? linkedInspection?.access_exit_id
              : linkedInspection?.access_entry_id;
            if (!otherId) return null;
            const otherTs = isEntry
              ? linkedInspection?.exit_timestamp
              : linkedInspection?.entry_timestamp;
            return (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate(`/truck-yard/access?event=${otherId}`);
                }}
                title={isEntry ? t("access.details.viewLinkedExit") : t("access.details.viewLinkedEntry")}
                className={`${surface} rounded-xl p-4 flex items-center gap-3 w-full text-left ${themeClasses.buttonHover} hover:ring-1 hover:ring-primary-500/40 transition-all`}
              >
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    isEntry ? "bg-red-500/20 text-red-500" : "bg-green-500/20 text-green-500"
                  }`}
                >
                  <i className={`${isEntry ? "ri-logout-box-line" : "ri-login-box-line"} text-lg`}></i>
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor}`}>
                    {isEntry ? t("access.details.linkedExit") : t("access.details.linkedEntry")}
                  </p>
                  <p className={`text-xs ${themeClasses.text} truncate`}>
                    {otherTs ? formatTimestamp(otherTs) : t("access.details.noTimestamp")}
                  </p>
                </div>
                <i className={`ri-arrow-right-s-line text-lg ${themeClasses.textSecondary} flex-shrink-0`}></i>
              </button>
            );
          })()}
        </div>

        <div className="sticky bottom-0 flex items-center justify-end px-6 py-4 border-t border-gray-700/30 bg-inherit rounded-b-2xl">
          <button onClick={onClose}
            className={`px-5 py-2.5 rounded-xl ${themeClasses.buttonHover} ${themeClasses.buttonText} font-medium text-sm transition-all border ${themeClasses.cardBorder}`}>
            {t("access.details.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Access Modal ─────────────────────────────────────────────────────────

// Los campos y sus reglas viven en components/ManualRegistrationFields.tsx y
// components/manualRegistration.ts: Inspecciones usa el MISMO formulario para
// el paso 1 de una pipa sin tag. Acá queda lo que es sólo de Accesos: el modal,
// y qué pasa al enviar (crear el evento y abrir la pluma, en handleNewAccess).
type NewAccessFormData = ManualRegistrationFormData;

function NewAccessModal({
  themeClasses,
  isDarkMode,
  yards,
  groups,
  vehicles,
  lockedYardId,
  onClose,
  onSubmit,
  submitting,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  yards: TruckYardOption[];
  groups: Group[];
  vehicles: TruckYardVehicle[];
  lockedYardId?: string;
  onClose: () => void;
  onSubmit: (data: NewAccessFormData) => void;
  submitting: boolean;
}) {
  const { t } = useTranslation("truckYard");
  const [form, setForm] = useState<NewAccessFormData>(() =>
    emptyManualRegistrationForm({ yard_id: lockedYardId || "" }),
  );
  const [errors, setErrors] = useState<ManualRegistrationErrors>({});

  // For ENTRY records the unit must be identified: either a catalog vehicle is
  // picked, or a plate was typed manually. Exits keep the looser behavior.
  const unitMissing = isUnitMissing(form);

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const found = validateManualRegistration(form);
    setErrors(found);
    if (Object.keys(found).length === 0) onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto`}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>{t("access.form.title")}</h2>
          <button onClick={onClose} className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}>
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <ManualRegistrationFields
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            form={form}
            errors={errors}
            onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
            onClearError={(field) => setErrors((p) => ({ ...p, [field]: undefined }))}
            yards={yards}
            groups={groups}
            vehicles={vehicles}
            lockedYardId={lockedYardId}
          />

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className={`flex-1 px-4 py-2.5 rounded-xl ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText} font-medium text-sm transition-colors`}>
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={submitting || unitMissing}
              className="flex-1 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: brand.primary, color: brand.accent }}>
              {submitting
                ? <><i className="ri-loader-4-line animate-spin mr-1"></i>{t("access.form.registering")}</>
                : <><i className={`mr-1 ${form.type === "entry" ? "ri-login-box-line" : "ri-logout-box-line"}`}></i>{form.type === "entry" ? t("access.form.registerEntry") : t("access.form.registerExit")}</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Sync Modal ──────────────────────────────────────────────────────────────
// Pulls a controller's stored access log for a window and backfills any events
// the live pipeline missed. One controller per yard; "all yards" loops them.

interface SyncFormData {
  yard_id: string; // "" → all yards
  allHistory: boolean;
  from: string;
  to: string;
}

function SyncModal({
  themeClasses,
  isDarkMode,
  yards,
  defaultYardId,
  lockedYardId,
  onClose,
  onSubmit,
  submitting,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  yards: TruckYardOption[];
  defaultYardId: string;
  lockedYardId?: string;
  onClose: () => void;
  onSubmit: (data: SyncFormData) => void;
  submitting: boolean;
}) {
  const { t } = useTranslation("truckYard");
  const range = defaultDateRange();
  const [form, setForm] = useState<SyncFormData>({
    yard_id: lockedYardId || defaultYardId || "",
    allHistory: false,
    from: range.from,
    to: range.to,
  });

  const surface = isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-50";
  const inputCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;
  const labelCls = `text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`;

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={submitting ? undefined : onClose} />
      <div className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto`}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>{t("access.sync.title")}</h2>
          <button onClick={onClose} disabled={submitting} className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors disabled:opacity-50`}>
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <p className={`text-sm ${themeClasses.textSecondary}`}>{t("access.sync.subtitle")}</p>

          {/* Yard */}
          <div>
            <label className={labelCls}>{t("access.sync.yard")}</label>
            <select
              value={form.yard_id}
              onChange={(e) => setForm((p) => ({ ...p, yard_id: e.target.value }))}
              disabled={!!lockedYardId}
              className={`${inputCls} ${lockedYardId ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              {!lockedYardId && <option value="">{t("access.sync.allYards")}</option>}
              {yards.map((y) => <option key={y.id} value={y.id}>{y.name}</option>)}
            </select>
          </div>

          {/* All-history toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.allHistory}
              onChange={(e) => setForm((p) => ({ ...p, allHistory: e.target.checked }))}
              className="w-4 h-4 accent-primary-500"
            />
            <span className={`text-sm ${themeClasses.text}`}>{t("access.sync.allHistory")}</span>
          </label>

          {/* Date range (disabled when all-history) */}
          {!form.allHistory && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>{t("access.sync.from")}</label>
                <input
                  type="date"
                  value={form.from}
                  onChange={(e) => setForm((p) => ({ ...p, from: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>{t("access.sync.to")}</label>
                <input
                  type="date"
                  value={form.to}
                  onChange={(e) => setForm((p) => ({ ...p, to: e.target.value }))}
                  className={inputCls}
                />
              </div>
            </div>
          )}

          <p className={`text-[11px] ${themeClasses.textSecondary} inline-flex items-start gap-1`}>
            <i className="ri-time-line mt-px"></i>
            <span>{t("access.sync.slowHint")}</span>
          </p>

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={submitting}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium ${surface} ${themeClasses.text} disabled:opacity-50`}>
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2.5 rounded-xl text-sm font-medium bg-primary-500 hover:bg-primary-400 text-gray-900 disabled:opacity-60 flex items-center">
              {submitting
                ? <><i className="ri-loader-4-line animate-spin mr-1"></i>{t("access.sync.submitting")}</>
                : <><i className="ri-refresh-line mr-1"></i>{t("access.sync.submit")}</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── QR Scanner ──────────────────────────────────────────────────────────────
// Disabled for now — the QR section was removed from the manual access form.
// Kept commented until we confirm whether to delete it permanently.
/*
function QRScanner({
  isDarkMode,
  themeClasses,
  onScan,
  onClose,
}: {
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  onScan: (data: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stopCamera = useCallback(() => {
    if (animationRef.current) { cancelAnimationFrame(animationRef.current); animationRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((track) => track.stop()); streamRef.current = null; }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        if ("BarcodeDetector" in window) {
          const detector = new (window as unknown as { BarcodeDetector: new (opts: { formats: string[] }) => { detect: (source: HTMLVideoElement) => Promise<{ rawValue: string }[]> } }).BarcodeDetector({ formats: ["qr_code"] });
          const scanFrame = async () => {
            if (cancelled || !videoRef.current) return;
            try {
              const barcodes = await detector.detect(videoRef.current);
              if (barcodes.length > 0 && barcodes[0].rawValue) { onScan(barcodes[0].rawValue); stopCamera(); return; }
            } catch {} // continue
            animationRef.current = requestAnimationFrame(scanFrame);
          };
          animationRef.current = requestAnimationFrame(scanFrame);
        }
      } catch { if (!cancelled) setError("No se pudo acceder a la cámara. Verifica los permisos del navegador."); }
    };
    startCamera();
    return () => { cancelled = true; stopCamera(); };
  }, [onScan, stopCamera]);

  return (
    <div className="space-y-3">
      <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-black">
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4">
            <i className="ri-camera-off-line text-3xl text-red-400"></i>
            <p className="text-sm text-red-400 text-center">{error}</p>
          </div>
        ) : (
          <>
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-48 border-2 border-green-400 rounded-2xl relative">
                <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-green-400 rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-green-400 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-green-400 rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-green-400 rounded-br-lg" />
                <div className="absolute left-2 right-2 h-0.5 bg-green-400/80 animate-pulse top-1/2" />
              </div>
            </div>
            <div className="absolute bottom-3 left-0 right-0 text-center">
              <span className="text-xs text-white bg-black/60 px-3 py-1 rounded-full">Apunta al código QR del vehículo</span>
            </div>
          </>
        )}
      </div>
      {!("BarcodeDetector" in window) && !error && (
        <p className={`text-xs ${themeClasses.textSecondary} text-center`}>Tu navegador no soporta detección de QR nativa. Usa Chrome 83+ o Edge 83+.</p>
      )}
      <button onClick={() => { stopCamera(); onClose(); }}
        className={`w-full py-2 rounded-xl text-sm font-medium ${isDarkMode ? "bg-dark-surface text-gray-300 hover:bg-dark-card" : "bg-gray-100 text-gray-700 hover:bg-gray-200"} transition-colors`}>
        <i className="ri-close-line mr-1"></i>Cerrar cámara
      </button>
    </div>
  );
}
*/

// ─── Pagination ──────────────────────────────────────────────────────────────

function AccessPagination({ currentPage, totalPages, onPageChange }: { currentPage: number; totalPages: number; onPageChange: (p: number) => void }) {
  const { t } = useTranslation("truckYard");
  if (totalPages <= 1) return null;
  return (
    <div className="flex justify-center mt-6 items-center gap-2">
      <button onClick={() => onPageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
        className="p-1.5 rounded-lg bg-gray-200 dark:bg-dark-card text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-300 dark:hover:bg-dark-surface transition-colors">
        <i className="ri-arrow-left-s-line text-sm"></i>
      </button>
      <span className="text-sm text-gray-500">{t("common.page", { current: currentPage, total: totalPages })}</span>
      <button onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}
        className="p-1.5 rounded-lg bg-gray-200 dark:bg-dark-card text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-300 dark:hover:bg-dark-surface transition-colors">
        <i className="ri-arrow-right-s-line text-sm"></i>
      </button>
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({ themeClasses }: { themeClasses: ThemeClasses }) {
  const { t } = useTranslation("truckYard");
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${themeClasses.cardBg}`}>
        <i className={`ri-door-lock-line text-4xl ${themeClasses.textSecondary}`}></i>
      </div>
      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`}>{t("access.empty.title")}</h3>
      <p className={`text-sm ${themeClasses.textSecondary} text-center max-w-xs`}>
        {t("access.empty.subtitle")}
      </p>
    </div>
  );
}

// ─── Loading Skeleton ────────────────────────────────────────────────────────

function LoadingSkeleton({ isDarkMode, view }: { isDarkMode: boolean; view: AccessView }) {
  const base = isDarkMode ? "bg-dark-card" : "bg-gray-200";
  const shimmer = isDarkMode ? "bg-dark-surface" : "bg-gray-300";
  if (view === "grid") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`${base} rounded-2xl p-5 animate-pulse`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-11 h-11 rounded-xl ${shimmer}`} />
              <div className="flex-1 space-y-2"><div className={`h-3 w-3/4 rounded ${shimmer}`} /><div className={`h-2.5 w-1/2 rounded ${shimmer}`} /></div>
            </div>
            <div className={`rounded-xl p-3 space-y-2 mb-4 ${shimmer}/30`}><div className={`h-2.5 w-2/3 rounded ${shimmer}`} /><div className={`h-2.5 w-1/2 rounded ${shimmer}`} /></div>
            <div className={`h-8 rounded-xl ${shimmer}`} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={`${base} rounded-xl p-4 animate-pulse flex items-center gap-4`}>
          <div className={`w-10 h-10 rounded-xl ${shimmer}`} />
          <div className="flex-1 space-y-2"><div className={`h-3 w-1/3 rounded ${shimmer}`} /><div className={`h-2.5 w-1/4 rounded ${shimmer}`} /></div>
          <div className={`h-6 w-16 rounded-full ${shimmer}`} />
        </div>
      ))}
    </div>
  );
}

// ─── Event Table (dense tabular view + bulk selection) ───────────────────────

interface EventTableRow {
  event: YardAccessEvent;
  alerts: EventAlert[];
  vehicle: TruckYardVehicle | null;
}

function EventTable({
  rows,
  canManage,
  selectedIds,
  onToggleOne,
  allFilteredSelected,
  someFilteredSelected,
  onToggleAllFiltered,
  themeClasses,
  isDarkMode,
  onDetails,
}: {
  rows: EventTableRow[];
  canManage: boolean;
  selectedIds: Set<string>;
  onToggleOne: (id: string) => void;
  allFilteredSelected: boolean;
  someFilteredSelected: boolean;
  onToggleAllFiltered: () => void;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (e: YardAccessEvent) => void;
}) {
  const { t } = useTranslation("truckYard");
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const headCls = `sticky top-0 z-10 px-3 py-2.5 text-[11px] uppercase tracking-wide ${themeClasses.textSecondary} font-medium border-b ${themeClasses.cardBorder} ${surface} text-left whitespace-nowrap`;
  const cellCls = `px-3 py-2.5 ${themeClasses.text} whitespace-nowrap`;

  return (
    <div className={`overflow-x-auto rounded-xl border ${themeClasses.cardBorder}`}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            {canManage && (
              <th className={`${headCls} w-10`}>
                <input
                  type="checkbox"
                  aria-label={t("access.table.selectAll")}
                  checked={allFilteredSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
                  }}
                  onChange={onToggleAllFiltered}
                  className="w-4 h-4 rounded accent-primary-500 cursor-pointer align-middle"
                />
              </th>
            )}
            <th className={headCls}>{t("access.table.type")}</th>
            <th className={headCls}>{t("access.table.vehicle")}</th>
            <th className={headCls}>{t("access.list.yard")}</th>
            <th className={headCls}>{t("access.list.dateTime")}</th>
            <th className={headCls}>{t("access.details.result")}</th>
            <th className={headCls}>{t("access.list.source")}</th>
            <th className={headCls}>{t("access.table.alerts")}</th>
            <th className={`${headCls} text-right`}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ event: e, alerts, vehicle }) => {
            const isEntry = e.event_type === "entry";
            const maxLevel = getMaxAlertLevel(alerts);
            const selected = selectedIds.has(e.id);
            const displayName =
              vehicle?.name || e.vehicle_plate || e.card_number || "—";
            const displaySub = vehicle
              ? [vehicle.plate, vehicle.brand].filter(Boolean).join(" · ")
              : e.driver_name || "";
            return (
              <tr
                key={e.id}
                className={`border-t ${themeClasses.cardBorder} transition-colors ${
                  selected
                    ? "bg-primary-500/10"
                    : `${themeClasses.cardBg} ${themeClasses.buttonHover}`
                }`}
              >
                {canManage && (
                  <td className={`${cellCls} w-10`} onClick={(ev) => ev.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`select-${e.id}`}
                      checked={selected}
                      onChange={() => onToggleOne(e.id)}
                      className="w-4 h-4 rounded accent-primary-500 cursor-pointer align-middle"
                    />
                  </td>
                )}
                <td className={cellCls}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full font-medium ${eventTypeClasses(e.event_type)}`}>
                      <i className={isEntry ? "ri-login-box-line" : "ri-logout-box-line"}></i>
                      {eventTypeLabel(e.event_type, t)}
                    </span>
                    <SuspiciousBadge event={e} />
                    <EventSpecialTagBadge event={e} />
                  </div>
                </td>
                <td className={cellCls}>
                  <div className="flex flex-col">
                    <span className={`font-medium ${themeClasses.text} truncate max-w-[200px]`}>{displayName}</span>
                    {displaySub && (
                      <span className={`text-xs ${themeClasses.textSecondary} truncate max-w-[200px]`}>{displaySub}</span>
                    )}
                  </div>
                </td>
                <td className={`${cellCls} ${themeClasses.textSecondary}`}>{e.yard_name || "—"}</td>
                <td className={`${cellCls} ${themeClasses.textSecondary}`}>{formatTimestamp(e.timestamp)}</td>
                <td className={cellCls}>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${resultClasses(e.event_result)}`}>
                    {resultLabel(e.event_result, t)}
                  </span>
                </td>
                <td className={`${cellCls} ${themeClasses.textSecondary}`}>{sourceLabel(e.source, t)}</td>
                <td className={cellCls}>
                  {maxLevel === "none" ? (
                    <span className={`text-xs ${themeClasses.textSecondary}`}>—</span>
                  ) : (
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${alertChipClasses(maxLevel)}`}>
                      <i className={`${alertIcon(maxLevel)} text-xs`}></i>
                      {alerts.length}
                    </span>
                  )}
                </td>
                <td className={`${cellCls} text-right`}>
                  <button
                    onClick={() => onDetails(e)}
                    className="w-7 h-7 rounded-full inline-flex items-center justify-center transition-colors"
                    style={{ backgroundColor: brand.primary }}
                    title={t("access.card.details")}
                  >
                    <i className="ri-eye-line text-white text-sm"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function AccessSection() {
  const { t } = useTranslation("truckYard");
  const { isDarkMode } = useDarkMode();

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const {
    accessEvents: events,
    eventsLoading: loading,
    setAccessEvents,
    setEventsLoading,
  } = useHikvisionAccessStore();

  const vehiclesFromStore = useTruckYardStore((s) => s.vehicles);
  const guardsFromStore = useTruckYardStore((s) => s.guards);
  const fetchYardUsers = useTruckYardStore((s) => s.fetchYardUsers);
  const yardsFromStore = useTruckYardStore((s) => s.yards);
  const fetchYards = useTruckYardStore((s) => s.fetchYards);
  const groups = useGroupsStore((s) => s.groups);
  useAllTruckYardVehicles();
  useAllGroups();

  // Lo que el usuario eligió, en crudo —ahora una LISTA: el combo es de
  // selección múltiple—. Los patios EFECTIVOS no son éstos: salen de
  // `resolveMultiYardScopedRequest` unas líneas más abajo, que los acota al
  // alcance (#496).
  const [yardPicks, setYardPicks] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState("");
  // Filters by Hikvision device (a.k.a. "control de accesos" — a physical
  // entry/exit point). The value is the device's serial number, which is
  // what events store in `hikvision_event.device_serial`.
  const [selectedDevice, setSelectedDevice] = useState("");
  const [devices, setDevices] = useState<HikvisionDevice[]>([]);
  // El rango arranca en el día en curso (ver defaultDateRange).
  const [dateFrom, setDateFrom] = useState(() => defaultDateRange().from);
  const [dateTo, setDateTo] = useState(() => defaultDateRange().to);
  // Filters events by derived alert level. Toggled by clicking the red/yellow
  // pills next to the page title.
  const [accessType, setAccessType] = useState<AccessTypeFilter | null>(null);
  const [alertFilter, setAlertFilter] = useState<AlertLevel | null>(null);
  const [selectedView, setSelectedView] = useState<AccessView>("grid");
  const [currentPage, setCurrentPage] = useState(1);
  // Bulk-selection of table rows for deletion (admins / reseller admins only).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [detailsEvent, setDetailsEvent] = useState<YardAccessEvent | null>(null);
  const [newAccessOpen, setNewAccessOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [yards, setYards] = useState<TruckYardOption[]>([]);
  // Inspections feed the alert engine (verifying the inspection link and the
  // exit→entry inheritance) and the per-card "linked inspection" + "linked
  // entry" rows. Refreshed by the polling tick alongside events.
  const [inspections, setInspections] = useState<YardInspection[]>([]);
  const [inspectionsLoaded, setInspectionsLoaded] = useState(false);
  // Si lo que llegó es TODO lo que el filtro abarca (issue #507). Arranca en
  // false —"no sé"— para que la primera respuesta lo decida, nunca una
  // suposición: es lo que separa "esta unidad entró sin inspección" de "esa
  // inspección no vino en la página".
  const [inspectionsComplete, setInspectionsComplete] = useState(false);

  const { isAdmin, getYardScope, canSelectAllYards } = useTruckYardRole();
  const { isReseller } = useIsReseller();
  // El alcance de patios como tres estados (ver resolveYardScope). Sale de
  // `hasYardRestrictions()`, que está gateado por compañía: un usuario de otra
  // empresa con el role_id de vigilante NO queda recortado — usar `isGuard`
  // crudo acá lo dejaba sin poder registrar accesos, y antes funcionaba.
  const yardScope = getYardScope();
  const noAssignedYards = yardScope.mode === "none";

  // El vigilante sin ningún patio no queda LIBRE, queda sin patios: no se le
  // ofrece ningún patio ni se le deja abrir los modales de alta y de sync.
  //
  // Dos formas anteriores de esto estuvieron mal. `isGuard && guardYardId ? ...
  // : undefined` dejaba `undefined` con la lista vacía, y el `<select>` de
  // patio se DESBLOQUEABA en NewAccessModal y SyncModal ofreciendo todos los
  // patios — y Accesos es el único menú del vigilante, así que no había otra
  // pantalla donde el recorte lo alcanzara. El reemplazo, un id centinela, era
  // peor: como cadena no vacía pasaba el `if (!form.yard_id)` de NewAccessModal
  // y `createManualAccessEvent` llegaba a PERSISTIR un evento bajo un patio
  // inexistente. Mostrar de más es un bug; escribir basura en la base es otra
  // cosa.
  //
  // Es alcanzable, no teórico: el balde `revisar-manual` de
  // `scripts/migrateUserYardIds.js` son justamente documentos cuyo `yard_ids`
  // no resuelve a nada, y ninguna de las dos fases de la migración los repara.
  const lockedYardId =
    yardScope.mode === "some" && yardScope.yardIds.length === 1
      ? yardScope.yardIds[0]
      : undefined;

  // Qué patios se le ofrecen. Con `none` no hay ninguno, y los botones que
  // abren los modales están deshabilitados.
  //
  // Es la MISMA lista para los modales y para el combo de filtro: mientras
  // fueron dos, el combo ofrecía los siete patios y `baseFiltered` ya descartaba
  // los ajenos, así que elegir uno fuera del alcance devolvía cero eventos — que
  // acá se lee como "no hubo movimiento" y no como "no es tuyo".
  //
  // ── El combo y el patio efectivo (issue #496) ─────────────────────────────
  //
  // El vigilante era el peor caso de los tres que enumera #496, y era un caso
  // a medio hacer: `lockedYardId` ya existía pero sólo trababa el `<select>` de
  // los modales de alta y de Sync. La GRILLA no lo miraba —`selectedYard`
  // arrancaba en `""` y nadie lo forzaba—, así que la petición salía sin
  // `yard_id`, el backend devolvía los eventos de los siete patios de la
  // empresa y recién `baseFiltered` los descartaba en el navegador. Con el
  // combo diciendo "Todos los patios" encima.
  //
  // `resolveYardFilter` se evalúa en RENDER y `selectedYardIds` es su salida, no
  // un estado propio: corregir el estado desde un `useEffect` deja un commit
  // intermedio con `""`, y los efectos que disparan `loadEvents` /
  // `loadInspections` corren en ESE commit — la primera petición saldría igual
  // sin `yard_id`. Ver el comentario de la función.
  //
  // `resolveMultiYardScopedRequest` (que delega en `resolveYardFilter`) y no
  // `resolveYardFilter` a secas: el
  // `skipYardQueries` que apaga las tres cargas de esta pantalla sale de la
  // MISMA decisión que el combo, en vez de releer `yardScope.mode` por su
  // cuenta.
  //
  // Lo que eso garantiza es acotado, y conviene no exagerarlo: `enabled` es
  // exactamente `scope.mode !== "none"`, así que un alcance restringido y sin
  // patios no puede terminar pidiendo. NO garantiza que la petición lleve
  // siempre `yard_id`: el supervisor en su "todos" sale con params vacíos y
  // habilitada a propósito —el recorte a los suyos lo sigue haciendo el
  // navegador—, y el admin también, porque su alcance ES la empresa. Los tres
  // casos están enumerados en yardScope.test.ts.
  //
  // Las cargas de abajo usan `yardParams` y NO `yardFilter.params`: `params`
  // es un objeto nuevo cada vez que esta memo se recalcula —lo hace al llegar
  // el catálogo, aunque el patio no cambie— y `eventQueryParams` es lo que
  // incrementa `filterGenerationRef`. Con el objeto en la dependencia, cada
  // montaje se ganaba un refetch con el filtro intacto. `yardParams` cuelga de
  // `selectedYardKey`, que compara por valor.
  const canAllYards = canSelectAllYards();
  const yardFilter = useMemo(
    () =>
      resolveMultiYardScopedRequest({
        scope: yardScope,
        canSelectAllYards: canAllYards,
        yards,
        selectedYardIds: yardPicks,
      }),
    [yardScope, canAllYards, yards, yardPicks],
  );
  // Con selección múltiple el patio efectivo es una LISTA, y un arreglo nuevo
  // en cada recálculo de la memo rompería la comparación por valor que la nota
  // de arriba exige (`yardFilter` se recalcula al llegar el catálogo aunque la
  // selección no cambie). La clave string es lo que compara por valor; la
  // lista y los params salen de ella, así que sólo cambian cuando cambia la
  // selección efectiva. Los ids son auto-ids de Firestore: no llevan comas.
  const selectedYardKey = yardFilter.selectedYardIds.join(",");
  const selectedYardIds = useMemo(
    () => (selectedYardKey ? selectedYardKey.split(",") : []),
    [selectedYardKey],
  );
  // Un patio viaja como `yard_id` (el contrato de siempre); varios, como
  // `yard_ids`. Mismo criterio que `yardFilter.params`, sin su identidad.
  const yardParams = useMemo<{ yard_id?: string; yard_ids?: string }>(
    () =>
      selectedYardIds.length === 0
        ? {}
        : selectedYardIds.length === 1
          ? { yard_id: selectedYardIds[0] }
          : { yard_ids: selectedYardIds.join(",") },
    [selectedYardIds],
  );
  // Sigue siendo `filterYardsByScope`, ahora por dentro de `resolveYardFilter`:
  // el combo y los `<select>` de los modales tienen que ofrecer exactamente lo
  // mismo, y mientras fueron dos listas divergieron.
  const selectableYards = yardFilter.options;
  // Who may delete records: system admins and reseller admins. Also gates the
  // bulk-selection checkboxes in the table view. Enforced again server-side.
  const canManage = isAdmin || isReseller;

  // ── El rango viaja al backend, no se recorta sólo en el navegador ─────────
  //
  // La carga inicial pedía `limit: 1000` a secas y el rango de fechas se
  // aplicaba recién en `baseFiltered`, en cliente. Dos consecuencias, las dos
  // silenciosas: el "día en curso" no reducía nada del lado del servidor, y si
  // el techo de 1000 se agotaba con eventos viejos la pantalla mostraba un día
  // INCOMPLETO sin decirlo. El backend ya acepta `date_from`/`date_to`/`yard_id`
  // (hikvisionAccessController.js:545-613) e interpreta la fecha sola como día
  // de pared del patio.
  //
  // `yard_id` sólo viaja cuando el usuario ya eligió patio: sin él, el recorte
  // por patio asignado es #495/#496, no de acá.
  //
  // El `limit: 1000` se queda: acota un día, no el histórico, y sigue cubriendo
  // el caso que lo motivó — un Sync del controlador que backfillea eventos con
  // timestamp viejo, que ahora simplemente caen fuera del rango del día.
  // `yardParams` y no `yardFilter.params` acá: ver la nota de `yardFilter`
  // sobre por qué un objeto en esta dependencia cuesta un refetch por montaje.
  const eventQueryParams = useMemo(
    () => ({
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
      ...yardParams,
    }),
    [dateFrom, dateTo, yardParams],
  );

  // ── Qué invalida una respuesta: el FILTRO, no el orden de emisión ─────────
  //
  // Esto empezó siendo un contador compartido por `loadEvents` y
  // `refreshEvents` con la regla "vale la última emitida", y estaba mal en las
  // dos direcciones:
  //
  //  · Perdía datos. El poll pide 100 y la carga completa 1000. Al montar, la
  //    carga sale primero y el tick de los 5 s puede contestar antes; cuando
  //    llegaban los 1000 se descartaban por ser "vieja", y el store quedaba con
  //    los 100 del poll — el "día incompleto y en silencio" que este trabajo
  //    vino a arreglar.
  //  · Dejaba el esqueleto puesto para siempre. `setEventsLoading(false)` sólo
  //    se escribe acá; si la respuesta se descartaba, el `finally` se lo
  //    salteaba y la lista quedaba en `LoadingSkeleton` de por vida.
  //
  // Lo que de verdad invalida una respuesta es que el FILTRO haya cambiado
  // mientras estaba en vuelo. Con la generación, la carga y el poll conviven:
  // los dos escriben si su filtro sigue vigente y el merge del poll hace el
  // resto, sin importar en qué orden resuelvan.
  //
  // El efecto que la incrementa va declarado ANTES de los que disparan las
  // cargas: los efectos de un mismo commit corren en orden de declaración, así
  // que cuando `loadEvents` corre en el commit del cambio de filtro ya ve la
  // generación nueva.
  const filterGenerationRef = useRef(0);
  useEffect(() => {
    filterGenerationRef.current++;
  }, [eventQueryParams]);

  // ── Sin patios asignados NO se pide nada ──────────────────────────────────
  //
  // Con `none` no hay `yard_id` que mandar, así que la petición saldría sin
  // filtro y el backend devolvería los eventos de los siete patios de la
  // empresa — que `baseFiltered` descarta uno por uno en el navegador. Es
  // exactamente el patrón que #496 prohíbe ("por defecto se carga sólo la
  // información de los patios asignados, en la petición, no recortando en el
  // navegador lo que ya se trajo"), y acá es el caso más caro: hasta 1000
  // eventos cada 5 s para renderizar una lista vacía bajo el cartel de "no
  // tenés patios asignados".
  //
  // Sale de la MISMA decisión que arma los params, y no de una lectura aparte
  // del alcance, para que las dos no puedan desincronizarse al editarse por
  // separado. Es el único caso en que "sin `yard_id`" y "no pedir" tienen que
  // ir juntos: con `all` —y con el "todos" del supervisor— la petición sin
  // `yard_id` es correcta y sí tiene que salir. Cubierto en yardScope.test.ts.
  const skipYardQueries = !yardFilter.enabled;

  // Fetch events
  const loadEvents = useCallback(async () => {
    if (skipYardQueries) {
      setAccessEvents([]);
      setEventsLoading(false);
      return;
    }
    const generation = filterGenerationRef.current;
    setEventsLoading(true);
    try {
      const result = await fetchYardAccessEvents({
        ...eventQueryParams,
        limit: 1000,
      });
      if (generation !== filterGenerationRef.current) return; // otro filtro
      setAccessEvents(result.data);
    } catch (err) {
      console.error("Error loading access events:", err);
    } finally {
      // Si el filtro cambió, ya hay otra carga corriendo que puso el esqueleto
      // y lo va a apagar; apagarlo acá lo haría parpadear.
      if (generation === filterGenerationRef.current) setEventsLoading(false);
    }
  }, [skipYardQueries, eventQueryParams, setAccessEvents, setEventsLoading]);

  // Silent refresh — used for polling so we don't flash the loading skeleton
  // every 5 seconds.
  //
  // Merged into the loaded set, never substituted for it. The poll asks for the
  // newest 100 while the initial load asks for 1000 (see above: a controller
  // Sync backfills events with OLD timestamps, which sort to the bottom), and
  // it used to hand that short page straight to setAccessEvents — a full
  // replace. Five seconds after opening the screen, up to 900 events silently
  // disappeared from every filter, count and export, which hit the Sync flow
  // hardest: the backfilled history appeared, then the next tick dropped it.
  // A date range reaching past the newest 100 went empty with nothing on
  // screen saying the rows still existed.
  //
  // Merging keeps the window and costs no extra bandwidth. Deletions still
  // propagate: the bulk-delete handler re-runs the full loadEvents().
  const refreshEvents = useCallback(async () => {
    if (skipYardQueries) return;
    const generation = filterGenerationRef.current;
    try {
      // Mismo rango que la carga inicial: si el poll pidiera sin filtro
      // volvería a meter en el store eventos fuera del día, que el cliente
      // tendría que descartar de nuevo en cada render.
      const result = await fetchYardAccessEvents({
        ...eventQueryParams,
        limit: 100,
      });
      // El poll NO toca `eventsLoading`: el esqueleto es de la carga completa.
      if (generation !== filterGenerationRef.current) return; // otro filtro
      const fresh = result.data;
      const freshIds = new Set(fresh.map((e) => e.id));
      const previous = useHikvisionAccessStore
        .getState()
        .accessEvents.filter((e) => !freshIds.has(e.id));
      // Fresh copies first, then the older tail. Nothing on this screen ever
      // re-sorts `events` — the grid, the table and the export all take the
      // API's order as given — so the merge has to preserve it rather than
      // impose one. It does: both sides come from the same endpoint under the
      // same ordering and the poll's page is that ordering's prefix, so the
      // remainder concatenates behind it without interleaving.
      setAccessEvents([...fresh, ...previous]);
    } catch (err) {
      console.error("Error refreshing access events:", err);
    }
  }, [skipYardQueries, eventQueryParams, setAccessEvents]);

  // ── Por qué esta pantalla sigue pidiendo inspecciones, y por qué SIN fecha ─
  //
  // El reparto de producto es "historial = sólo inspecciones, accesos = sólo
  // eventos", y esta pantalla no lista inspecciones: no hay grilla, tabla ni
  // export de inspecciones acá. Lo que hace con ellas es resolver el vínculo de
  // cada EVENTO, y eso no se puede quitar sin apagar cosas visibles:
  //
  //  · las alertas `inspection_unknown` (el evento apunta a una inspección que
  //    no está) y `exit_no_entry` / `exit_no_entry_ts` (salida cuya visita no
  //    tiene entrada) dejarían de dispararse;
  //  · la fila "Inspección vinculada" y la de entrada/salida vinculada de cada
  //    tarjeta y del modal de detalle dirían "sin inspección" en eventos que sí
  //    la tienen.
  //
  // Tampoco se puede resolver por evento, bajo demanda: el módulo no expone un
  // `GET /yardInspection?id=` —sólo `/yardInspectionList`, el PUT y las rutas de
  // evidencia— y, aunque lo expusiera, las píldoras de alerta cuentan sobre
  // TODO `baseFiltered` y no sobre la página visible, así que resolver sólo lo
  // visible haría que los conteos dependieran de en qué página está parado el
  // operador.
  //
  // Y no lleva `date_from`/`date_to` a propósito, aunque los eventos sí: la
  // inspección de un evento NO tiene por qué ser del día del evento. El rango de
  // `/yardInspectionList` se ancla a la fecha de ENTRADA (#492), y en este patio
  // hay visitas que quedan abiertas días —11 de las 20 tarjetas de Acuapue nunca
  // fueron leídas por la lectora de salida, y hay folios abiertos 120 h, 106 h y
  // 34 h—. Cuando una de ésas por fin registra su salida, el evento de HOY
  // apunta a una inspección cuya entrada es de la semana pasada: recortando las
  // inspecciones al día se marcaría `inspection_unknown` justo en el caso que el
  // operador más necesita ver bien.
  //
  // Sí viaja `yard_id` cuando hay patio elegido: la inspección se crea con el
  // `yard_id` del evento, así que recortar por patio no puede desalinear las dos
  // ventanas, y baja lo que viaja.
  //
  // ── Lo que este `limit: 500` degrada después de un Sync (va en #507) ──
  //
  // Desde que `/yardInspectionList` ordena por fecha de ENTRADA, estas 500 son
  // las 500 más recientes POR ENTRADA, no las 500 escritas más recientemente.
  // Un Sync del controlador backfillea visitas con timestamps viejos, así que
  // sus inspecciones —recién creadas— ordenan al fondo y se caen de la página 1
  // en cuanto una empresa o patio pase de 500 inspecciones. Justo después de un
  // sync, `handleSync` amplía el rango de eventos para mostrar esos eventos
  // viejos, y sus inspecciones ya no están en el mapa: alertas amarillas
  // `inspection_unknown` sobre exactamente los eventos que el operador está
  // revisando. Con el orden por ingest esos documentos estaban arriba.
  //
  // No es alcanzable hoy (145 inspecciones en producción contra un techo de
  // 500) pero el sync es la vía principal de ingesta, así que el flujo que se
  // degrada es el que más importa.
  //
  // NO se arregla acá, y la razón es que toda variante dentro de esta pantalla
  // mueve el falso positivo en vez de sacarlo. La opción evaluada fue mandar el
  // mismo rango que los eventos cuando ése no es el día en curso: arregla el
  // caso del backfill, pero rompe el simétrico —la visita que entró ANTES de la
  // ventana y salió adentro ancla fuera y no se trae—, que hoy funciona o no
  // según el mismo techo de 500. Cambia qué instancias caen en la clase, no la
  // clase.
  //
  // Lo que sí la elimina es que el faltante se resuelva por id
  // (`GET /yardInspection?id=`, que el módulo no expone) o que
  // `/yardAccessEventList` devuelva el estado de la inspección junto a cada
  // evento. Las dos son los caminos que ya propone #507, y cualquiera de las
  // dos deja además sacar este barrido de 500 por completo.
  //
  // Declarado acá arriba (sobre el efecto de polling que lo usa) para no leer
  // `loadInspections` en TDZ durante el primer render.
  // La secuencia descarta las respuestas viejas. Bajar `inspectionsLoaded` al
  // cambiar de patio no alcanza: la respuesta del patio ANTERIOR, que sigue en
  // vuelo, llegaba después del reset, repoblaba `inspections` con el conjunto
  // equivocado y volvía a subir la bandera — con lo que reaparecía el parpadeo
  // de `inspection_unknown` en todas las tarjetas, que es justo lo que el reset
  // venía a evitar. Pasa en cuanto el operador cambia de patio mientras hay un
  // tick del poll en curso, y el poll corre cada POLL_INTERVAL_MS (5 s).
  const inspectionsRequestRef = useRef(0);
  const loadInspections = useCallback(async () => {
    if (skipYardQueries) return;
    const seq = ++inspectionsRequestRef.current;
    let data: YardInspection[] | null = null;
    let complete = false;
    try {
      const result = await fetchYardInspections({
        ...yardParams,
        limit: 500,
      });
      data = result.data;
      // El backend informa el total del filtro sin truncar, así que la pantalla
      // puede SABER si lo que tiene es todo en vez de suponerlo (issue #507).
      // `total` y `data` salen del MISMO arreglo en la misma request —
      // `total = filtered.length` y `data = filtered.slice(start, start + 500)`,
      // hikvisionAccessController.js:755-757—, así que hoy sólo pueden ser
      // iguales o quedarse corto; el `>=` es para que esto siga siendo cierto si
      // alguna vez el total pasa a venir de un conteo aparte. Sin `pagination`
      // se asume incompleto, que es el lado seguro: no afirmar.
      //
      // Sólo vale porque se pide la página 1: con `page > 1` una respuesta
      // completa traería 500 de N y `complete` daría falso negativo. No hay
      // llamada paginada acá y no debería haberla — ver el comentario de arriba
      // sobre por qué este barrido no se pagina.
      complete =
        typeof result.pagination?.total === "number" &&
        data.length >= result.pagination.total;
    } catch (err) {
      console.error("Error loading yard inspections:", err);
    }
    if (seq !== inspectionsRequestRef.current) return; // respuesta vieja

    // La bandera se levanta SÓLO si de verdad llegó el conjunto. Subirla tras
    // un fallo, combinada con el reset que vacía `inspections` al cambiar de
    // patio, dejaba el mapa vacío y la bandera en alto: `deriveAlerts` emitía
    // entonces `inspection_unknown` amarillo en TODA tarjeta con
    // `inspection_id` — el parpadeo que ese reset existe para prevenir, ahora
    // disparado por una sola petición fallida. Antes de este trabajo un fallo
    // conservaba el mapa anterior, así que este camino lo abrí yo.
    //
    // Dejarla en false hace que los chequeos que dependen de inspecciones se
    // salteen hasta que llegue un conjunto real, que es exactamente para lo que
    // está. El siguiente tick del poll la cura.
    if (!data) return;
    setInspections(data);
    setInspectionsComplete(complete);
    setInspectionsLoaded(true);
  }, [skipYardQueries, yardParams]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Al cambiar de patio, el mapa de inspecciones que hay en memoria es el del
  // patio ANTERIOR. `loadInspections` ahora depende de los patios elegidos, pero
  // `inspectionsLoaded` no volvía a false, así que durante el hueco entre el
  // cambio de filtro y la respuesta los eventos del patio nuevo se evaluaban
  // contra el mapa viejo: ninguno de sus `inspection_id` estaba ahí y la
  // pantalla parpadeaba una alerta `inspection_unknown` amarilla en TODAS las
  // tarjetas. Bajar la bandera apaga los chequeos que dependen de inspecciones
  // hasta que llegue el conjunto correcto, que es justo para lo que existe.
  //
  // Este efecto va ANTES del que dispara la carga para que el reset ocurra en
  // el mismo commit que el cambio de patio, no un render después.
  useEffect(() => {
    // Invalida además lo que esté en vuelo para el patio anterior: sin esto la
    // respuesta tardía repuebla el mapa que se acaba de limpiar.
    inspectionsRequestRef.current++;
    setInspections([]);
    setInspectionsLoaded(false);
    setInspectionsComplete(false);
  }, [selectedYardKey]);

  useEffect(() => { loadInspections(); }, [loadInspections]);

  // ── Deep link `?event=<id>` ───────────────────────────────────────────────
  //
  // Lo usa YardInspectionDetailsModal para saltar de la inspección al evento de
  // entrada o de salida que la originó. Con el fetch acotado al día en curso,
  // ese evento puede no estar en `events`: la entrada vinculada de una visita
  // abierta hace tres días es de hace tres días. El efecto se quedaba esperando
  // un `match` que nunca llegaba y encima NO limpiaba el query param, así que el
  // enlace quedaba muerto y pegado en la URL.
  //
  // Acá sí hay endpoint por id (`GET /yardAccessEvent`), así que el evento se
  // pide explícitamente en vez de ampliar el rango: abre el modal sin tocar lo
  // que el operador está mirando. El evento traído se mete además en el store
  // para que el modal resuelva sus alertas y su vehículo como cualquier otro.
  //
  // El param se limpia SIEMPRE, incluso si la petición falla: dejarlo puesto
  // hace que cada render reintente para siempre.
  //
  // Dos detalles que hacían que NO abriera nunca, y por eso están así:
  //
  //  · `events` NO va en las dependencias. Iba, y cambia con cada tick del poll
  //    y con el `loadEvents` del montaje: el efecto se volvía a correr,
  //    el cleanup cancelaba el fetch en vuelo y la nueva corrida salía por el
  //    guard del ref, así que el modal no abría, el param no se limpiaba y no
  //    había reintento. El atajo "ya está en la lista" lee del store con
  //    `getState()`, que no crea dependencia.
  //  · El ref se marca al TERMINAR, no antes del await. Marcarlo antes lo
  //    rompía de forma determinista en StrictMode: el doble invoke cancelaba la
  //    primera corrida y la segunda se auto-bloqueaba. Marcándolo al final, la
  //    corrida cancelada no deja rastro y la segunda completa.
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedEventRef = useRef<string | null>(null);
  useEffect(() => {
    const requestedId = searchParams.get("event");
    if (!requestedId) return;
    if (autoOpenedEventRef.current === requestedId) return;

    const clearParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete("event");
      setSearchParams(next, { replace: true });
    };

    const known = useHikvisionAccessStore.getState().accessEvents;
    const match = known.find((e) => e.id === requestedId);
    if (match) {
      autoOpenedEventRef.current = requestedId;
      setDetailsEvent(match);
      clearParam();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const fetched = await fetchYardAccessEvent(requestedId);
        if (cancelled) return;
        if (fetched) {
          const current = useHikvisionAccessStore.getState().accessEvents;
          if (!current.some((e) => e.id === fetched.id)) {
            setAccessEvents([fetched, ...current]);
          }
          setDetailsEvent(fetched);
        }
      } catch (err) {
        console.error("Error loading linked access event:", err);
      } finally {
        if (!cancelled) {
          autoOpenedEventRef.current = requestedId;
          clearParam();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, setAccessEvents]);

  // ── Tiempo real: señal RTDB + poll de respaldo ────────────────────────────
  // Dos vías disparan el MISMO tick (`refreshEvents` + `loadInspections`):
  //
  //   1. La señal `yardAccessLive/{yardId}` de la RTDB, que el backend escribe
  //      por cada evento ingerido. No trae el evento: sólo avisa, y el dato
  //      sigue saliendo de /yardAccessEventList con sus filtros y su recorte
  //      por patio. Las señales se juntan en ventanas de 500 ms.
  //   2. El poll de siempre. A 5 s mientras la señal no esté sana; a 30 s
  //      cuando sí, para cubrir lo que la señal no ve (ver liveSignal.ts).
  //
  // La señal es una mejora, no una dependencia: las reglas de la RTDB para
  // `yardAccessLive` viven en la consola de Firebase y NO se pudieron
  // verificar desde el repo. Si el `onValue` da permission_denied, o la RTDB
  // nunca conecta, la pantalla se queda en el poll de 5 s de antes, sin
  // mostrar error (un console.warn por montaje).
  //
  // Las reglas de pausa no cambian: con un modal abierto o "En vivo" apagado
  // no se refresca. Una señal que llega en pausa queda anotada en
  // `pendingSignalRef` y se atiende apenas se reanuda, sin esperar al poll.
  //
  // Lo que queda pendiente del TODO anterior: una suscripción directa a
  // `yardAccessEvents` (onSnapshot) evitaría el fetch por señal, pero exige el
  // cliente de Firestore en v5 y reglas por company_id que hoy no existen.
  const [livePolling, setLivePolling] = useState(true);
  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null);
  const [liveSignalHealthy, setLiveSignalHealthy] = useState(false);
  // Señal recibida y todavía no atendida por un tick.
  const pendingSignalRef = useRef(false);
  // "Corré el tick ya". Lo publica el efecto del poll mientras está activo y lo
  // retira al pausarse, así el listener no necesita conocer las reglas de pausa.
  const runTickNowRef = useRef<(() => void) | null>(null);
  const liveSignalWarnedRef = useRef(false);

  useEffect(() => {
    if (!livePolling) return;
    if (detailsEvent || newAccessOpen || syncOpen) return; // pause while a modal is open

    let cancelled = false;
    let timeoutId: number | undefined;
    let inFlight = false;
    const intervalMs = pollIntervalMs(liveSignalHealthy);

    const scheduleNext = () => {
      if (cancelled) return;
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(runTick, intervalMs);
    };

    const runTick = async () => {
      if (cancelled) return;
      // Una señal que cae con un fetch en curso puede ser de un evento que ese
      // fetch ya no ve: se anota y se repite el tick al terminar, en vez de
      // pisarse con dos fetches simultáneos de la misma lista.
      if (inFlight) {
        pendingSignalRef.current = true;
        return;
      }
      if (document.hidden) {
        // La señal pendiente sobrevive: `onVisible` corre el tick al volver.
        scheduleNext();
        return;
      }
      window.clearTimeout(timeoutId);
      pendingSignalRef.current = false;
      inFlight = true;
      try {
        await Promise.all([refreshEvents(), loadInspections()]);
        if (!cancelled) setLastRefreshAt(new Date());
      } finally {
        inFlight = false;
        if (!cancelled && pendingSignalRef.current) runTick();
        else scheduleNext();
      }
    };

    const onVisible = () => {
      if (document.hidden || cancelled) return;
      runTick();
    };
    document.addEventListener("visibilitychange", onVisible);

    const runNow = () => {
      runTick();
    };
    runTickNowRef.current = runNow;

    // Al reanudar (cerró el modal, volvió "En vivo") con una señal anotada en
    // la pausa, refrescamos ya; si no, el poll arranca con su intervalo.
    if (pendingSignalRef.current) runTick();
    else scheduleNext();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisible);
      if (runTickNowRef.current === runNow) runTickNowRef.current = null;
    };
  }, [
    livePolling,
    detailsEvent,
    newAccessOpen,
    syncOpen,
    refreshEvents,
    loadInspections,
    liveSignalHealthy,
  ]);

  // Qué patios escuchar. La clave string compara por valor (mismo motivo que
  // `selectedYardKey`): `selectableYards` se recalcula al llegar el catálogo y
  // re-suscribir por eso reiniciaría las marcas sin necesidad.
  const liveSignalTarget = useMemo(
    () =>
      liveSignalYardIds({
        skip: skipYardQueries,
        selectedYardIds,
        optionYardIds: selectableYards.map((y) => y.id),
      }),
    [skipYardQueries, selectedYardIds, selectableYards],
  );
  const liveSignalKey = liveSignalTarget.yardIds.join(",");
  const liveSignalComplete = liveSignalTarget.complete;

  useEffect(() => {
    const yardIds = liveSignalKey ? liveSignalKey.split(",") : [];
    if (yardIds.length === 0) return;

    let disposed = false;
    let coalesceId: number | undefined;
    let connected = false;
    const lastTs = new Map<string, number>();
    const ready = new Set<string>();
    const errored = new Set<string>();

    const evaluate = () => {
      if (disposed) return;
      setLiveSignalHealthy(
        isLiveSignalHealthy({
          complete: liveSignalComplete,
          connected,
          subscribed: yardIds.length,
          ready: ready.size,
          errored: errored.size,
        }),
      );
    };

    const fire = () => {
      coalesceId = undefined;
      if (disposed) return;
      pendingSignalRef.current = true;
      // Sin `runTickNowRef` el poll está en pausa: la marca queda y la atiende
      // el efecto del poll al reanudarse.
      runTickNowRef.current?.();
    };

    const unsubscribes: Array<() => void> = [];

    // `.info/connected` es la única forma de distinguir "conectado y sin
    // pases" de "nunca conectó": un `onValue` sin red no da error, sólo calla.
    unsubscribes.push(
      onValue(rtdbRef(database, ".info/connected"), (snap) => {
        connected = snap.val() === true;
        evaluate();
      }),
    );

    for (const yardId of yardIds) {
      unsubscribes.push(
        onValue(
          rtdbRef(database, `yardAccessLive/${yardId}`),
          (snap) => {
            ready.add(yardId);
            const decision = shouldTriggerRefresh(lastTs.get(yardId), snap.val());
            lastTs.set(yardId, decision.lastTs);
            evaluate();
            if (decision.trigger && coalesceId === undefined) {
              coalesceId = window.setTimeout(fire, LIVE_SIGNAL_COALESCE_MS);
            }
          },
          (err) => {
            // permission_denied cancela el listener. El poll de 5 s sigue
            // cubriendo el patio; avisamos una vez y nada más.
            errored.add(yardId);
            if (!liveSignalWarnedRef.current) {
              liveSignalWarnedRef.current = true;
              console.warn(
                "[accesos] señal en vivo yardAccessLive no disponible; se sigue con el poll de 5 s:",
                err,
              );
            }
            evaluate();
          },
        ),
      );
    }

    return () => {
      disposed = true;
      window.clearTimeout(coalesceId);
      for (const unsubscribe of unsubscribes) unsubscribe();
      // Hasta que el nuevo juego de listeners demuestre estar sano, el poll
      // vuelve a 5 s.
      setLiveSignalHealthy(false);
    };
  }, [liveSignalKey, liveSignalComplete]);

  // Fetch yards for dropdown
  useEffect(() => {
    const loadYards = async () => {
      try {
        setYards(await fetchTruckYardOptions());
      } catch {
        setYards([]);
      }
    };
    loadYards();
  }, []);

  // Fetch Hikvision devices ("controles de acceso") for the device filter.
  // Re-fetches when the yard filter changes so the dropdown is scoped to the
  // selected yard; when no yard is selected, we load devices across the
  // company (limited to 200 — enough for any realistic deployment).
  useEffect(() => {
    // Sin patios asignados tampoco hay controladores propios que listar, y sin
    // `yard_id` esto traería los de toda la empresa (ver skipYardQueries).
    if (skipYardQueries) {
      setDevices([]);
      return;
    }
    let cancelled = false;
    const loadDevices = async () => {
      try {
        // `/hikvisionDeviceList` sólo entiende un `yard_id`. Con varios patios
        // se piden los de la empresa (el mismo pedido que "todos") y se
        // recortan acá a los de la selección: un controlador tiene UN patio.
        const { data: fetched } = await fetchHikvisionDevices({
          ...(selectedYardIds.length === 1 ? { yard_id: selectedYardIds[0] } : {}),
          limit: 100,
          page: 1,
        });
        if (cancelled) return;
        const data =
          selectedYardIds.length > 1
            ? fetched.filter((d) => selectedYardIds.includes(d.yard_id))
            : fetched;
        setDevices(data);
        // Drop a selection the new yard cannot offer, here rather than in a
        // separate effect watching `devices`. That effect had to skip an empty
        // list (an empty `devices` is also what a not-yet-resolved fetch and a
        // failed one look like) — and an empty list is precisely the case that
        // needed clearing: move to a yard with no controllers and the dropdown
        // falls back to its "todos los controles" placeholder, with no clear
        // "x" because the option is gone, while the stale serial keeps
        // filtering the grid to zero. Empty results, a filter bar claiming no
        // filter, and no way out but "Limpiar filtros". Doing it on a resolved
        // response makes the empty case unambiguous, and the functional update
        // reads the current selection without making it a dependency (which
        // would refetch the device list on every pick).
        setSelectedDevice((prev) =>
          prev && !data.some((d) => d.device_serial === prev) ? "" : prev,
        );
      } catch {
        // Deliberately leave `devices` and the selection alone: a failed fetch
        // is not evidence that the yard has no controllers.
      }
    };
    loadDevices();
    return () => {
      cancelled = true;
    };
  }, [skipYardQueries, selectedYardIds]);

  // Fetch vehicles + groups (needed for alert derivation and filters)
  // Guards + yards are needed to compute on-duty vigilantes per event.
  useEffect(() => {
    fetchYardUsers("guards", { limit: 500, page: 1 });
    fetchYards();
  }, [fetchYardUsers, fetchYards]);


  // Indexes for fast lookup
  const vehiclesById = useMemo(() => {
    const map = new Map<string, TruckYardVehicle>();
    vehiclesFromStore.forEach((v) => map.set(v.id, v));
    return map;
  }, [vehiclesFromStore]);

  const yardsById = useMemo(() => {
    const map = new Map<string, TruckYard>();
    yardsFromStore.forEach((y) => map.set(y.id, y));
    return map;
  }, [yardsFromStore]);

  const onDutyVigilantes = useMemo(() => {
    if (!detailsEvent) return [] as TruckYardUser[];
    const ts = parseTimestamp(detailsEvent.timestamp);
    if (!ts) return [];
    const tz = yardsById.get(detailsEvent.yard_id)?.timeZone;
    return guardsFromStore.filter((g) =>
      isGuardOnDuty(g, ts, detailsEvent.yard_id, tz),
    );
  }, [detailsEvent, guardsFromStore, yardsById]);

  const inspectionsById = useMemo(() => {
    const map = new Map<string, YardInspection>();
    inspections.forEach((i) => map.set(i.id, i));
    return map;
  }, [inspections]);

  // Per-event vigilante predicate used by the alert engine. Returns true
  // while guards are still loading so we don't flash a transient
  // "no_vigilante" alert across every event on first paint.
  const isVigilanteOnDuty = useCallback(
    (event: YardAccessEvent) => {
      if (guardsFromStore.length === 0) return true;
      const ts = parseTimestamp(event.timestamp);
      if (!ts) return true;
      const tz = yardsById.get(event.yard_id)?.timeZone;
      return guardsFromStore.some((g) =>
        isGuardOnDuty(g, ts, event.yard_id, tz),
      );
    },
    [guardsFromStore, yardsById],
  );

  // Joins on `vehicle_ids`, NOT `devices_ids`. The set is tested against
  // `event.vehicle_id` below, and devices_ids holds device document IDs — the
  // groupsStore says so in as many words, and the water-type join twelve lines
  // down already gets it right. With the wrong field the intersection was
  // always empty, so picking any transportista blanked the list and zeroed
  // both alert pills: the filter did nothing except hide everything.
  const groupVehicleIds = useMemo(() => {
    if (!selectedGroup) return null;
    const group = groups.find((g) => g.id === selectedGroup);
    return new Set(group?.vehicle_ids || []);
  }, [selectedGroup, groups]);

  // Water types inherited by each vehicle from its groups (union across all
  // groups the vehicle belongs to). Joins on `vehicle_ids` — devices_ids
  // contains device document IDs, not vehicle IDs.
  const vehicleInheritedTypes = useMemo(() => {
    const map = new Map<string, string[]>();
    groups.forEach((g) => {
      const groupTypes = g.type || [];
      if (groupTypes.length === 0) return;
      (g.vehicle_ids || []).forEach((vehicleId) => {
        const existing = map.get(vehicleId) || [];
        map.set(vehicleId, Array.from(new Set([...existing, ...groupTypes])));
      });
    });
    return map;
  }, [groups]);

  // Pre-compute alerts + resolved variables per event. Each variable feeds
  // a "data present?" row inside the cards — present values render as
  // success; missing/invalid values render as the matching alert.
  const eventsWithAlerts = useMemo(() => {
    const ctx: DeriveAlertsContext = {
      vehiclesById,
      inspectionsById,
      inspectionsLoaded,
      inspectionsComplete,
      isVigilanteOnDuty,
      vehicleInheritedTypes,
      yardsById,
    };
    return events.map((e) => {
      const linkedInspection = e.inspection_id
        ? inspectionsById.get(e.inspection_id) ?? null
        : null;
      const ts = parseTimestamp(e.timestamp);
      const tz = yardsById.get(e.yard_id)?.timeZone;
      const onDutyGuards =
        ts && guardsFromStore.length > 0
          ? guardsFromStore.filter((g) => isGuardOnDuty(g, ts, e.yard_id, tz))
          : ([] as TruckYardUser[]);
      const vehicle = e.vehicle_id ? vehiclesById.get(e.vehicle_id) ?? null : null;
      const inherited = vehicle ? vehicleInheritedTypes.get(vehicle.id) || [] : [];
      const vehicleTypes = effectiveVehicleTypes(vehicle, inherited);
      const yard = yardsById.get(e.yard_id) || null;
      const yardTypes = yard?.type || [];
      return {
        event: e,
        alerts: deriveAlerts(e, ctx, t),
        vehicle,
        timestamp: ts,
        linkedInspection,
        onDutyGuards,
        vehicleTypes,
        yardTypes,
      };
    });
  }, [
    events,
    vehiclesById,
    inspectionsById,
    inspectionsLoaded,
    inspectionsComplete,
    isVigilanteOnDuty,
    guardsFromStore,
    yardsById,
    vehicleInheritedTypes,
    t,
  ]);

  // ── El recorte del cliente usa el MISMO día que el del backend ────────────
  //
  // Este filtro sigue haciendo falta —el poll fusiona su página con lo que ya
  // había en el store (ver refreshEvents), así que al cambiar el rango queda
  // cola del rango anterior hasta que `loadEvents` reemplace el conjunto, y sin
  // esto esa cola se contaría en las píldoras—, pero no es "redundante": era un
  // SEGUNDO recorte con otra definición de día.
  //
  // `new Date("2026-09-01T00:00:00")` es medianoche en la zona del NAVEGADOR;
  // el backend interpreta esa misma fecha como medianoche del PATIO
  // (yardDateRange.js, la corrección de #472). Con las dos zonas distintas el
  // cliente descartaba en silencio la cola del día que el servidor sí había
  // devuelto — el bug de #472 otra vez, de este lado del cable. Ver
  // yardDayBounds.ts.
  //
  // La zona es la del patio elegido; sin patio elegido, el mismo fallback que
  // usa el backend cuando no recibe `yard_id`. Con varios, la del PRIMERO de la
  // selección, que es el mismo que usa el backend (`resolveYardTimeZone(
  // requestedYardIds[0])` en /yardAccessEventList): los dos lados tienen que
  // cortar el mismo día, o vuelve #472.
  const filterYardTimeZone =
    (selectedYardIds[0] ? yardsById.get(selectedYardIds[0])?.timeZone : null) ||
    DEFAULT_YARD_TIME_ZONE;

  // ── El default se recalcula: ni se congela al montar, ni pisa al operador ──
  //
  // Dos motivos para volver a evaluarlo:
  //
  //  · Medianoche. El inicializador de `useState` corre una vez, así que la
  //    pestaña abierta al cruzar las 00:00 se quedaba pidiendo el día de ayer.
  //    Antes daba igual porque el rango sólo recortaba en el navegador; ahora
  //    viaja al backend, y ésta es una pantalla de vigilancia que se deja
  //    abierta el turno entero.
  //  · Cambio de patio. Al montar no hay patio elegido y el default sale del
  //    fallback; cuando el operador elige uno con otra zona, "hoy" puede ser
  //    otro día.
  //
  // Se exige que AMBOS extremos sigan valiendo el default vigente antes de
  // tocar nada. Compararlos por separado —como estaba— le movía "Hasta" a las
  // 00:00 al operador que sólo había cambiado "Desde": o los dos siguen siendo
  // el default, o no se toca ninguno.
  const appliedDefaultRangeRef = useRef(defaultDateRange(filterYardTimeZone));
  useEffect(() => {
    const apply = () => {
      const next = defaultDateRange(filterYardTimeZone);
      const applied = appliedDefaultRangeRef.current;
      if (next.from === applied.from && next.to === applied.to) return;
      if (dateFrom !== applied.from || dateTo !== applied.to) return;
      appliedDefaultRangeRef.current = next;
      setDateFrom(next.from);
      setDateTo(next.to);
    };
    apply(); // cambio de zona del patio
    const id = window.setInterval(apply, 60000); // medianoche
    return () => window.clearInterval(id);
  }, [filterYardTimeZone, dateFrom, dateTo]);

  const dateBounds = useMemo(
    () => yardDateBounds(dateFrom, dateTo, filterYardTimeZone),
    [dateFrom, dateTo, filterYardTimeZone],
  );

  // Two-stage filtering: `baseFiltered` is everything except the alert pill
  // filter, so the alert counts stay stable as the user toggles pills.
  const baseFiltered = useMemo(
    () =>
      eventsWithAlerts.filter(({ event: e, timestamp }) => {
        // Recorte por patio asignado, ANTES del filtro que elige el usuario
        // (issue #497). No estaba: la lista sólo se acotaba por `selectedYard`,
        // así que un vigilante veía los registros de todos los patios — y con
        // el cartel de "sin patios asignados" arriba diciéndole lo contrario.
        if (yardScope.mode === "none") return false;
        if (yardScope.mode === "some" && !yardScope.yardIds.includes(e.yard_id))
          return false;
        // También recorta acá, y no sólo en la petición: un backend todavía sin
        // `yard_ids` lo ignora y devuelve todos los patios del alcance.
        if (selectedYardIds.length > 0 && !selectedYardIds.includes(e.yard_id))
          return false;
        if (selectedVehicle && e.vehicle_id !== selectedVehicle) return false;
        if (selectedDevice && e.hikvision_event?.device_serial !== selectedDevice) {
          return false;
        }
        if (accessType && !matchesAccessType(e, accessType)) return false;
        if (groupVehicleIds && (!e.vehicle_id || !groupVehicleIds.has(e.vehicle_id))) {
          return false;
        }
        if (dateBounds.from !== null || dateBounds.to !== null) {
          const ms = timestamp?.getTime() ?? null;
          if (ms === null) return false;
          if (dateBounds.from !== null && ms < dateBounds.from) return false;
          if (dateBounds.to !== null && ms > dateBounds.to) return false;
        }
        return true;
      }),
    [
      eventsWithAlerts,
      yardScope,
      selectedYardIds,
      selectedVehicle,
      selectedDevice,
      accessType,
      groupVehicleIds,
      dateBounds,
    ],
  );

  const filtered = useMemo(() => {
    if (!alertFilter) return baseFiltered;
    return baseFiltered.filter(({ alerts }) => getMaxAlertLevel(alerts) === alertFilter);
  }, [baseFiltered, alertFilter]);

  // Export rows/columns: the whole filtered set (not just the visible page),
  // formatted like the on-screen table.
  const exportColumns = useMemo<ReportTableColumn<AccessEventExportRow>[]>(
    () => [
      { key: "type", header: t("access.table.type") },
      { key: "vehicle", header: t("access.table.vehicle") },
      { key: "driver", header: "Conductor" },
      { key: "yard", header: t("access.list.yard") },
      { key: "datetime", header: t("access.list.dateTime") },
      { key: "result", header: t("access.details.result") },
      { key: "source", header: t("access.list.source") },
      { key: "alerts", header: t("access.table.alerts") },
    ],
    [t],
  );
  const exportRows = useMemo<AccessEventExportRow[]>(
    () =>
      filtered.map(({ event: e, alerts, vehicle }) => ({
        id: e.id,
        type: eventTypeLabel(e.event_type, t),
        vehicle: vehicle?.name || e.vehicle_plate || e.card_number || "—",
        driver: e.driver_name || "—",
        yard: e.yard_name || "—",
        datetime: formatTimestamp(e.timestamp),
        result: resultLabel(e.event_result, t),
        source: sourceLabel(e.source, t),
        alerts: alerts.length,
      })),
    [filtered, t],
  );

  const alertCounts = useMemo(() => {
    let red = 0;
    let yellow = 0;
    baseFiltered.forEach(({ alerts }) => {
      const level = getMaxAlertLevel(alerts);
      if (level === "red") red++;
      else if (level === "yellow") yellow++;
    });
    return { red, yellow };
  }, [baseFiltered]);

  const yardOptions = useMemo<SelectOption[]>(
    () => selectableYards.map((y) => ({ value: y.id, label: y.name })),
    [selectableYards],
  );
  const groupOptions = useMemo<SelectOption[]>(
    () => groups.map((g) => ({ value: g.id, label: g.name })),
    [groups],
  );
  const vehicleOptions = useMemo<SelectOption[]>(
    () =>
      vehiclesFromStore.map((v) => ({
        value: v.id,
        label: v.plate ? `${v.name || v.id} · ${v.plate}` : v.name || v.id,
      })),
    [vehiclesFromStore],
  );
  const deviceOptions = useMemo<SelectOption[]>(
    () =>
      devices.map((d) => {
        const doorTag =
          d.door_type === "entry"
            ? ` · ${t("accessControl.doorType.entry")}`
            : d.door_type === "exit"
              ? ` · ${t("accessControl.doorType.exit")}`
              : ` · ${t("accessControl.doorType.bidirectional")}`;
        return {
          value: d.device_serial,
          label: `${d.device_name || d.device_serial}${doorTag}`,
        };
      }),
    [devices, t],
  );

  const clearFilters = () => {
    // La zona sale del patio que queda DESPUÉS de limpiar, y por eso se vuelve
    // a preguntar en vez de reusar `filterYardTimeZone`. Antes de #496 limpiar
    // sacaba el patio siempre y el default se armaba con el fallback; ahora, con
    // un inspector o un vigilante, `setYardPicks([])` no lo saca — el mismo
    // render se lo devuelve resuelto contra su alcance —, así que el fallback
    // pediría el "hoy" de México contra un `yard_id` que el backend lee en la
    // zona del patio. Y para un admin pasa lo contrario: `filterYardTimeZone`
    // todavía vale la del patio que está por soltar. El único valor correcto es
    // el del patio de después, que es lo que devuelve esta llamada.
    //
    // El mismo par que se escribe en los inputs se guarda como default vigente.
    //
    // Guardarlo es obligatorio, no cosmético. El ref puede haber derivado —el
    // operador elige un patio en America/Tijuana a las 00:30 de México, el
    // efecto de arriba reescribe los dos inputs al "ayer" de Tijuana y lo
    // anota— y entonces limpiar dejaba `dateFrom` = hoy de México contra un
    // `applied` = ayer de Tijuana. Desde ahí `apply()` salía siempre por
    // `dateFrom !== applied.from` y el rango NO volvía a rotar a medianoche en
    // toda la sesión, que es justo la falla que ese efecto vino a arreglar.
    const clearedYardId = resolveYardFilter({
      scope: yardScope,
      canSelectAllYards: canAllYards,
      yards,
      selectedYardId: "",
    }).selectedYardId;
    const clearedYardTimeZone =
      (clearedYardId ? yardsById.get(clearedYardId)?.timeZone : null) ||
      DEFAULT_YARD_TIME_ZONE;
    const { from, to } = defaultDateRange(clearedYardTimeZone);
    appliedDefaultRangeRef.current = { from, to };
    // Vuelve al patio por DEFECTO del alcance, no a "todos": para un inspector
    // o un vigilante "todos" no existe, y `resolveYardFilter` reclama este ""
    // como su patio asignado en el mismo render (issue #496).
    setYardPicks([]);
    setSelectedGroup("");
    setSelectedVehicle("");
    setDateFrom(from);
    setDateTo(to);
    setAlertFilter(null);
    setSelectedDevice("");
    setAccessType(null);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Clamp, don't slice blind. `setCurrentPage(1)` is wired to every filter
  // control, but the result set also shrinks with no filter touched: delete
  // the contents of the last page and the reload leaves you on a page that no
  // longer exists — empty grid, header still counting a non-zero total,
  // earlier pages still full. Same on any poll that returns fewer rows.
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // ── Bulk selection (table view, admins / reseller admins) ─────────────────
  const filteredIds = useMemo(
    () => filtered.map(({ event }) => event.id),
    [filtered],
  );
  // Drop any selection that leaves the filtered set (filter/date/search change)
  // so a hidden row can never be deleted out from under the user.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(filteredIds);
      const next = new Set<string>();
      prev.forEach((id) => visible.has(id) && next.add(id));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredIds]);

  const allFilteredSelected =
    filteredIds.length > 0 && selectedIds.size === filteredIds.length;
  const someFilteredSelected = selectedIds.size > 0;

  const toggleSelectOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllFiltered = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === filteredIds.length ? new Set() : new Set(filteredIds),
    );
  }, [filteredIds]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    const result = await Swal.fire({
      title: t("access.deleteRecords.confirmTitle"),
      html: t("access.deleteRecords.confirmText", { count }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("access.deleteRecords.confirmButton"),
      cancelButtonText: t("common.cancel"),
    });
    if (!result.isConfirmed) return;
    setDeleting(true);
    try {
      const res = await deleteYardAccessEvents({ ids: Array.from(selectedIds) });
      clearSelection();
      await Promise.all([loadEvents(), loadInspections()]);
      Swal.fire({
        icon: "success",
        title: t("access.deleteRecords.successTitle"),
        html: t("access.deleteRecords.successText", {
          events: res.deletedEvents,
          inspections: res.deletedInspections,
        }),
        timer: 2600,
        showConfirmButton: false,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t("access.messages.unknownError");
      Swal.fire({ icon: "error", title: t("common.error"), text: message });
    } finally {
      setDeleting(false);
    }
  }, [selectedIds, t, clearSelection, loadEvents, loadInspections]);

  const bulkActions: BulkAction[] = [
    {
      id: "delete",
      label: t("access.deleteRecords.button"),
      icon: "ri-delete-bin-line",
      variant: "danger",
      disabled: deleting,
      onClick: handleDeleteSelected,
    },
  ];

  // Authoritative "last DB sync" time, stamped in Firestore on every manual/auto
  // sync run and read back from the backend (not inferred from loaded records).
  const [lastDbSyncAt, setLastDbSyncAt] = useState<Date | null>(null);
  const loadSyncStatus = useCallback(async () => {
    try {
      const { last_sync_at } = await fetchYardSyncStatus();
      setLastDbSyncAt(last_sync_at ? new Date(last_sync_at) : null);
    } catch {
      /* leave the previous value on a transient failure */
    }
  }, []);
  useEffect(() => {
    loadSyncStatus();
    const id = window.setInterval(loadSyncStatus, 30000);
    return () => window.clearInterval(id);
  }, [loadSyncStatus]);

  // Create manual event + trigger the door command for the matching device(s).
  // The device is routed by `door_type`: entry events open entry/bidirectional
  // doors; exit events open exit/bidirectional doors. Within a bidirectional
  // device the door comes from its reader map (doorForDirection). Door failures don't
  // fail the event registration — they're surfaced as a warning toast.
  // Reintentos de pluma por "SDK busy" en vuelo: se cortan al salir de la
  // pantalla, para que la pluma no se mueva cuando ya nadie la mira.
  // Un controlador por apertura: dos altas seguidas abren las dos plumas, y al
  // salir de la pantalla se cortan todas.
  const doorAborts = useRef(new Set<AbortController>());
  useEffect(() => {
    const aborts = doorAborts.current;
    return () => aborts.forEach((a) => a.abort());
  }, []);

  const handleNewAccess = async (data: NewAccessFormData) => {
    setSubmitting(true);
    try {
      // When a catalog vehicle is picked, resolve its plate so the companion
      // inspection (assignment history) is populated alongside vehicle_id.
      // Otherwise fall back to the manually typed plate.
      const resolvedPlate = resolveRegistrationPlate(data, vehiclesFromStore);
      await createManualAccessEvent({
        yard_id: data.yard_id,
        event_type: data.type,
        // vehicle_id is preferred by the backend when pairing the companion
        // inspection (assignment history); vehicle_plate stays as a fallback.
        vehicle_id: data.vehicle_id || undefined,
        vehicle_plate: resolvedPlate || undefined,
        driver_name: data.driver_name || undefined,
        card_number: data.card_number || undefined,
        notes: data.notes || undefined,
      });
      setNewAccessOpen(false);
      // El evento ya quedó: el formulario se libera aunque la pluma tarde (con
      // el controlador ocupado los reintentos pueden durar hasta 2 min).
      setSubmitting(false);
      Swal.fire({
        icon: "success",
        title: t("access.messages.registeredTitle"),
        text: data.type === "entry"
          ? t("access.messages.registeredEntryText")
          : t("access.messages.registeredExitText"),
        timer: 5000,
        showConfirmButton: true,
        confirmButtonText: t("access.details.close"),
        confirmButtonColor: "#10b981",
      });
      await loadEvents();

      // Levanta la pluma de esta dirección (ver openYardGate: puerta por mapa de
      // lectoras, reintento con el controlador ocupado, nunca lanza).
      const doorAbort = new AbortController();
      doorAborts.current.add(doorAbort);
      try {
        await openYardGate({ yardId: data.yard_id, direction: data.type, t, signal: doorAbort.signal });
      } finally {
        doorAborts.current.delete(doorAbort);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("access.messages.unknownError");
      Swal.fire({ icon: "error", title: t("common.error"), text: message });
    } finally {
      setSubmitting(false);
    }
  };

  // Backfill the DB from the controller's stored log, reusing the read-only
  // history pipeline: pull records (granted + denied), then persist the missing
  // ones — each entry triggering its inspection. One device per yard; "all
  // yards" loops them sequentially, accumulating a combined summary.
  const handleSync = async (data: SyncFormData) => {
    setSyncing(true);
    try {
      const targetYards = data.yard_id
        ? yards.filter((y) => y.id === data.yard_id)
        : yards;
      if (targetYards.length === 0) {
        Swal.fire({ icon: "info", title: t("access.sync.title"), text: t("access.sync.noYards") });
        return;
      }

      const from = data.allHistory || !data.from ? null : new Date(data.from).toISOString();
      const to = data.allHistory || !data.to
        ? null
        : new Date(`${data.to}T23:59:59`).toISOString();

      const totals: HistorySyncSummary = {
        created: 0, skipped: 0, entries: 0, exits: 0, inspections: 0, total: 0,
      };
      const skips = { notAccepted: 0, notDecision: 0, noIdentity: 0, duplicate: 0, noDevice: 0 };
      const failures: string[] = [];

      for (const y of targetYards) {
        const { data: devices } = await fetchHikvisionDevices({ yard_id: y.id, limit: 1, page: 1 });
        const device = devices[0];
        if (!device) {
          failures.push(`${y.name}: ${t("access.sync.noDevice")}`);
          continue;
        }
        // 1. Pull the controller's stored log (granted + denied credential decisions).
        const history = await fetchControllerHistoryAndWait({
          device_id: device.id,
          from,
          to,
          max: 2000,
          filter: "access",
        });
        if (history.status !== "completed") {
          failures.push(`${y.name}: ${history.error || t("access.sync.failed")}`);
          continue;
        }
        // The on-site bridge answered with a DIFFERENT controller than this
        // device (single-bridge site): skip so its log isn't re-stamped onto
        // this yard as duplicates of the controller that really owns it.
        if (
          history.controllerSerial &&
          device.device_serial &&
          history.controllerSerial !== device.device_serial
        ) {
          failures.push(`${y.name}: ${t("access.sync.serialMismatch")}`);
          continue;
        }
        // 2. Persist the ones the live pipeline missed (idempotent server-side).
        const s = await syncControllerEventsToDb(device.id, history.events);
        totals.created += s.created;
        totals.skipped += s.skipped;
        totals.entries += s.entries;
        totals.exits += s.exits;
        totals.inspections += s.inspections;
        totals.total += s.total;
        if (s.skippedReasons) {
          skips.notAccepted += s.skippedReasons.notAccepted;
          skips.notDecision += s.skippedReasons.notDecision;
          skips.noIdentity += s.skippedReasons.noIdentity;
          skips.duplicate += s.skippedReasons.duplicate;
          skips.noDevice += s.skippedReasons.noDevice;
        }
      }

      setSyncOpen(false);

      // Reveal the freshly synced events: they carry their original (historical)
      // controller timestamps, but the list defaults to the current day — so
      // widen it to the synced range, otherwise "created N" but nothing
      // appears. All-history clears the lower bound entirely.
      //
      // El extremo superior se arma en la zona del patio, igual que el default:
      // es la fecha que viaja al backend.
      //
      // Y si el rango cambia, la carga NO se dispara desde acá. `handleSync` se
      // recrea en cada render, así que el `loadEvents` que capturó lleva el
      // `eventQueryParams` de ESE render: llamarlo después de mover las fechas
      // pedía el día en curso y reemplazaba el store con eso, mientras el
      // re-render cambiaba la identidad de `loadEvents` y el efecto disparaba
      // una segunda petición con el rango ampliado. Dos peticiones en vuelo
      // sobre el mismo slot; cuando ganaba la angosta, el operador veía
      // "creados N" y una lista vacía hasta el siguiente tick del poll — el
      // modo de falla que el comentario del merge dice estar previniendo.
      //
      // El guard de secuencia ya impide que gane la vieja, pero la petición de
      // más igual sobra: cuando el rango cambia, el efecto que observa
      // `eventQueryParams` es el único que carga, y con el rango correcto.
      const nextFrom = data.allHistory ? "" : data.from;
      const nextTo = data.to || yardTodayDateInput(filterYardTimeZone);
      const rangeChanges =
        totals.created > 0 && (nextFrom !== dateFrom || nextTo !== dateTo);

      if (totals.created > 0) {
        setDateFrom(nextFrom);
        setDateTo(nextTo);
        setCurrentPage(1);
      }

      if (!rangeChanges) await loadEvents();
      loadSyncStatus();

      const summaryHtml = t("access.sync.summary", {
        created: totals.created,
        skipped: totals.skipped,
        inspections: totals.inspections,
      });
      // When nothing new was created but records were skipped, show the reason
      // breakdown so it's clear whether they were already-existing (duplicate)
      // or filtered out (not a card decision, no identity, etc.).
      const skipDetail =
        totals.created === 0 && totals.skipped > 0
          ? `<br/><span style="opacity:.75;font-size:12px">` +
            `dup ${skips.duplicate} · noDevice ${skips.noDevice} · ` +
            `notDecision ${skips.notDecision} · noIdentity ${skips.noIdentity} · notAccepted ${skips.notAccepted}</span>`
          : "";
      const failuresHtml = failures.length
        ? `<br/><span style="color:#f59e0b">${failures.map(escapeHtml).join("<br/>")}</span>`
        : "";
      Swal.fire({
        icon: totals.total === 0 && failures.length > 0 ? "warning" : "success",
        title: t("access.sync.resultTitle"),
        html: `${summaryHtml}${skipDetail}${failuresHtml}`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("access.messages.unknownError");
      Swal.fire({ icon: "error", title: t("common.error"), text: message });
    } finally {
      setSyncing(false);
    }
  };

  return (
    // Un solo contenedor con scroll: el header de filtros se va con las
    // tarjetas en vez de quedarse clavado arriba.
    <div className="h-full overflow-y-auto relative">
      {noAssignedYards && (
        <div className="px-4 sm:px-6 pt-4">
          <NoAssignedYardsNotice
            isDarkMode={isDarkMode}
            what="ningún acceso"
          />
        </div>
      )}
      <AccessHeader
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        selectedYardIds={selectedYardIds}
        selectedGroup={selectedGroup}
        selectedVehicle={selectedVehicle}
        selectedDevice={selectedDevice}
        accessType={accessType}
        dateFrom={dateFrom}
        dateTo={dateTo}
        yardOptions={yardOptions}
        yardLocked={yardFilter.locked}
        allowAllYards={yardFilter.allowAll}
        groupOptions={groupOptions}
        vehicleOptions={vehicleOptions}
        deviceOptions={deviceOptions}
        selectedView={selectedView}
        alertFilter={alertFilter}
        onYardChange={(v) => { setYardPicks(v); setCurrentPage(1); }}
        onGroupChange={(v) => { setSelectedGroup(v); setCurrentPage(1); }}
        onVehicleChange={(v) => { setSelectedVehicle(v); setCurrentPage(1); }}
        onDeviceChange={(v) => { setSelectedDevice(v); setCurrentPage(1); }}
        onAccessTypeChange={(v) => { setAccessType(v); setCurrentPage(1); }}
        onDateFromChange={(v) => { setDateFrom(v); setCurrentPage(1); }}
        onDateToChange={(v) => { setDateTo(v); setCurrentPage(1); }}
        onClearFilters={clearFilters}
        onViewChange={setSelectedView}
        onAlertFilterChange={(v) => {
          setAlertFilter(v);
          setCurrentPage(1);
        }}
        // El guard se conserva además del `disabled`: el botón es la única vía
        // hoy, pero el estado del modal lo puede abrir cualquier otra cosa que
        // se agregue después, y persistir un acceso sin patio válido fue un
        // fallo real de esta misma rama.
        onAdd={() => {
          if (!noAssignedYards) setNewAccessOpen(true);
        }}
        onSync={() => {
          if (!noAssignedYards) setSyncOpen(true);
        }}
        actionsDisabled={noAssignedYards}
        actionsDisabledReason={t("access.noAssignedYardsAction")}
        total={filtered.length}
        loading={loading}
        alertCounts={alertCounts}
      />

      {/* Live indicator + export */}
      <div className="px-4 sm:px-6 pt-3 -mb-1 flex flex-wrap items-center justify-end gap-2">
        <ExportButtons
          rows={exportRows}
          columns={exportColumns}
          meta={{
            title: t("access.title"),
            period:
              dateFrom || dateTo
                ? `Período: ${dateFrom || "inicio"} — ${dateTo || "hoy"}`
                : `${filtered.length} evento(s)`,
            fileBase: "eventos_acceso",
          }}
        />
        {lastDbSyncAt && (
          <span
            title={t("access.live.lastSyncTitle")}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.textSecondary}`}
          >
            {/* History, not a database cylinder: the pill answers "when", and
                the storage metaphor was the visual half of the same jargon. */}
            <i className="ri-history-line"></i>
            {t("access.live.lastSync", {
              time: lastDbSyncAt.toLocaleString("es-MX", {
                dateStyle: "short",
                timeStyle: "short",
              }),
            })}
          </span>
        )}
        <button
          type="button"
          onClick={() => setLivePolling((p) => !p)}
          title={livePolling ? t("access.live.pauseTitle") : t("access.live.resumeTitle")}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all ${
            livePolling
              ? "bg-green-500/15 border-green-500/30 text-green-400"
              : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.textSecondary}`
          }`}
        >
          <span className={`relative inline-flex w-2 h-2`}>
            {livePolling && (
              <span className="absolute inline-flex w-full h-full rounded-full bg-green-400 opacity-75 animate-ping" />
            )}
            <span className={`relative inline-flex w-2 h-2 rounded-full ${livePolling ? "bg-green-400" : "bg-gray-500"}`} />
          </span>
          {livePolling ? t("access.live.live") : t("access.live.paused")}
          {lastRefreshAt && livePolling && (
            <span className={`hidden sm:inline ${themeClasses.textSecondary} font-normal ml-1`}>
              · {lastRefreshAt.toLocaleTimeString("es-MX")}
            </span>
          )}
        </button>
      </div>

      <div className={`p-4 sm:p-6 ${someFilteredSelected ? "pb-24" : ""}`}>
        {loading ? (
          <LoadingSkeleton isDarkMode={isDarkMode} view={selectedView} />
        ) : paginated.length === 0 ? (
          <EmptyState themeClasses={themeClasses} />
        ) : selectedView === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginated.map(({ event: e, alerts, vehicle: v, linkedInspection: li, onDutyGuards: og, vehicleTypes: vt, yardTypes: yt }) => (
              <EventGridCard
                key={e.id}
                event={e}
                alerts={alerts}
                vehicle={v ?? null}
                linkedInspection={li}
                onDutyGuards={og}
                vehicleTypes={vt}
                yardTypes={yt}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                onDetails={setDetailsEvent}
              />
            ))}
          </div>
        ) : selectedView === "table" ? (
          <EventTable
            rows={paginated.map(({ event: e, alerts, vehicle: v }) => ({
              event: e,
              alerts,
              vehicle: v ?? null,
            }))}
            canManage={canManage}
            selectedIds={selectedIds}
            onToggleOne={toggleSelectOne}
            allFilteredSelected={allFilteredSelected}
            someFilteredSelected={someFilteredSelected}
            onToggleAllFiltered={toggleSelectAllFiltered}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onDetails={setDetailsEvent}
          />
        ) : (
          <div className="space-y-3">
            {paginated.map(({ event: e, alerts, vehicle: v, linkedInspection: li, onDutyGuards: og, vehicleTypes: vt, yardTypes: yt }) => (
              <EventListRow
                key={e.id}
                event={e}
                alerts={alerts}
                vehicle={v ?? null}
                linkedInspection={li}
                onDutyGuards={og}
                vehicleTypes={vt}
                yardTypes={yt}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                onDetails={setDetailsEvent}
              />
            ))}
          </div>
        )}

        <AccessPagination currentPage={safePage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </div>

      {/* Bulk selection bar — only for admins / reseller admins with a selection. */}
      {canManage && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          actions={bulkActions}
          onClearSelection={clearSelection}
          isDarkMode={isDarkMode}
        />
      )}

      {detailsEvent && (
        <EventDetailsModal
          event={detailsEvent}
          alerts={deriveAlerts(detailsEvent, {
            vehiclesById,
            inspectionsById,
            inspectionsLoaded,
            inspectionsComplete,
            isVigilanteOnDuty,
            vehicleInheritedTypes,
            yardsById,
          }, t)}
          vehicle={detailsEvent.vehicle_id ? vehiclesById.get(detailsEvent.vehicle_id) ?? null : null}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          vigilantes={onDutyVigilantes}
          linkedInspection={
            detailsEvent.inspection_id
              ? inspectionsById.get(detailsEvent.inspection_id) ?? null
              : null
          }
          onClose={() => setDetailsEvent(null)}
        />
      )}

      {newAccessOpen && !noAssignedYards && (
        <NewAccessModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          yards={selectableYards}
          groups={groups}
          vehicles={vehiclesFromStore}
          lockedYardId={lockedYardId}
          onClose={() => setNewAccessOpen(false)}
          onSubmit={handleNewAccess}
          submitting={submitting}
        />
      )}

      {syncOpen && !noAssignedYards && (
        <SyncModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          yards={selectableYards}
          // El modal sincroniza UN patio o todos: con uno elegido arranca en
          // ése; con varios, en "todos" si el rol lo permite, y si no en el
          // primero de la selección.
          defaultYardId={
            selectedYardIds.length === 1 || !yardFilter.allowAll
              ? selectedYardIds[0] || ""
              : ""
          }
          lockedYardId={lockedYardId}
          onClose={() => setSyncOpen(false)}
          onSubmit={handleSync}
          submitting={syncing}
        />
      )}
    </div>
  );
}
